import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import Select from 'react-select';
import ReactTooltip from 'react-tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './components/ui/dialog';
import { Button } from './components/ui/button';
import { useToast } from './hooks/use-toast';
import { FaDiscord } from 'react-icons/fa'; // Make sure react-icons is installed
// Add these imports for the history functionality
import { 
  ChevronDown, 
  Copy, 
  X
} from 'lucide-react';

import moment from 'moment';
import { generatePDF } from './labelGenerator';
import { BoxContext } from './BoxContext'; // Ensure BoxContext provides necessary data
import CountrySelector from './components/CountrySelector';
import USAImportInfo from './components/USAImportInfo';
import ExpirationDatePicker from './components/ExpirationDatePicker';

// Define countries array at the module level
const countries = [
  { value: 'AF', label: 'Afghanistan' }, { value: 'AL', label: 'Albania' }, { value: 'DZ', label: 'Algeria' },
  { value: 'AS', label: 'American Samoa' }, { value: 'AD', label: 'Andorra' }, { value: 'AO', label: 'Angola' },
  { value: 'AI', label: 'Anguilla' }, { value: 'AQ', label: 'Antarctica' }, { value: 'AG', label: 'Antigua and Barbuda' },
  { value: 'AR', label: 'Argentina' }, { value: 'AM', label: 'Armenia' }, { value: 'AW', label: 'Aruba' },
  { value: 'AU', label: 'Australia' }, { value: 'AT', label: 'Austria' }, { value: 'AZ', label: 'Azerbaijan' },
  { value: 'BS', label: 'Bahamas' }, { value: 'BH', label: 'Bahrain' }, { value: 'BD', label: 'Bangladesh' },
  { value: 'BB', label: 'Barbados' }, { value: 'BY', label: 'Belarus' }, { value: 'BE', label: 'Belgium' },
  { value: 'BZ', label: 'Belize' }, { value: 'BJ', label: 'Benin' }, { value: 'BM', label: 'Bermuda' },
  { value: 'BT', label: 'Bhutan' }, { value: 'BO', label: 'Bolivia' }, { value: 'BA', label: 'Bosnia and Herzegovina' },
  { value: 'BW', label: 'Botswana' }, { value: 'BV', label: 'Bouvet Island' }, { value: 'BR', label: 'Brazil' },
  { value: 'IO', label: 'British Indian Ocean Territory' }, { value: 'BN', label: 'Brunei Darussalam' }, { value: 'BG', label: 'Bulgaria' },
  { value: 'BF', label: 'Burkina Faso' }, { value: 'BI', label: 'Burundi' }, { value: 'KH', label: 'Cambodia' },
  { value: 'CM', label: 'Cameroon' }, { value: 'CA', label: 'Canada' }, { value: 'CV', label: 'Cape Verde' },
  { value: 'KY', label: 'Cayman Islands' }, { value: 'CF', label: 'Central African Republic' }, { value: 'TD', label: 'Chad' },
  { value: 'CL', label: 'Chile' }, { value: 'CN', label: 'China' }, { value: 'CX', label: 'Christmas Island' },
  { value: 'CC', label: 'Cocos (Keeling) Islands' }, { value: 'CO', label: 'Colombia' }, { value: 'KM', label: 'Comoros' },
  { value: 'CG', label: 'Congo' }, { value: 'CD', label: 'Congo, Democratic Republic of the' }, { value: 'CK', label: 'Cook Islands' },
  { value: 'CR', label: 'Costa Rica' }, { value: 'CI', label: "Côte d'Ivoire" }, { value: 'HR', label: 'Croatia' },
  { value: 'CU', label: 'Cuba' }, { value: 'CY', label: 'Cyprus' }, { value: 'CZ', label: 'Czech Republic' },
  { value: 'DK', label: 'Denmark' }, { value: 'DJ', label: 'Djibouti' }, { value: 'DM', label: 'Dominica' },
  { value: 'DO', label: 'Dominican Republic' }, { value: 'EC', label: 'Ecuador' }, { value: 'EG', label: 'Egypt' },
  { value: 'SV', label: 'El Salvador' }, { value: 'GQ', label: 'Equatorial Guinea' }, { value: 'ER', label: 'Eritrea' },
  { value: 'EE', label: 'Estonia' }, { value: 'ET', label: 'Ethiopia' }, { value: 'FK', label: 'Falkland Islands' },
  { value: 'FO', label: 'Faroe Islands' }, { value: 'FJ', label: 'Fiji' }, { value: 'FI', label: 'Finland' },
  { value: 'FR', label: 'France' }, { value: 'GF', label: 'French Guiana' }, { value: 'PF', label: 'French Polynesia' },
  { value: 'TF', label: 'French Southern Territories' }, { value: 'GA', label: 'Gabon' }, { value: 'GM', label: 'Gambia' },
  { value: 'GE', label: 'Georgia' }, { value: 'DE', label: 'Germany' }, { value: 'GH', label: 'Ghana' },
  { value: 'GI', label: 'Gibraltar' }, { value: 'GR', label: 'Greece' }, { value: 'GL', label: 'Greenland' },
  { value: 'GD', label: 'Grenada' }, { value: 'GP', label: 'Guadeloupe' }, { value: 'GU', label: 'Guam' },
  { value: 'GT', label: 'Guatemala' }, { value: 'GG', label: 'Guernsey' }, { value: 'GN', label: 'Guinea' },
  { value: 'GW', label: 'Guinea-Bissau' }, { value: 'GY', label: 'Guyana' }, { value: 'HT', label: 'Haiti' },
  { value: 'HM', label: 'Heard Island and McDonald Islands' }, { value: 'VA', label: 'Holy See (Vatican City State)' },
  { value: 'HN', label: 'Honduras' }, { value: 'HK', label: 'Hong Kong' }, { value: 'HU', label: 'Hungary' },
  { value: 'IS', label: 'Iceland' }, { value: 'IN', label: 'India' }, { value: 'ID', label: 'Indonesia' },
  { value: 'IR', label: 'Iran' }, { value: 'IQ', label: 'Iraq' }, { value: 'IE', label: 'Ireland' },
  { value: 'IM', label: 'Isle of Man' }, { value: 'IL', label: 'Israel' }, { value: 'IT', label: 'Italy' },
  { value: 'JM', label: 'Jamaica' }, { value: 'JP', label: 'Japan' }, { value: 'JE', label: 'Jersey' },
  { value: 'JO', label: 'Jordan' }, { value: 'KZ', label: 'Kazakhstan' }, { value: 'KE', label: 'Kenya' },
  { value: 'KI', label: 'Kiribati' }, { value: 'KP', label: "Korea, Democratic People's Republic of" }, { value: 'KR', label: 'Korea, Republic of' },
  { value: 'KW', label: 'Kuwait' }, { value: 'KG', label: 'Kyrgyzstan' }, { value: 'LA', label: "Lao People's Democratic Republic" },
  { value: 'LV', label: 'Latvia' }, { value: 'LB', label: 'Lebanon' }, { value: 'LS', label: 'Lesotho' },
  { value: 'LR', label: 'Liberia' }, { value: 'LY', label: 'Libya' }, { value: 'LI', label: 'Liechtenstein' },
  { value: 'LT', label: 'Lithuania' }, { value: 'LU', label: 'Luxembourg' }, { value: 'MO', label: 'Macao' },
  { value: 'MK', label: 'North Macedonia' }, { value: 'MG', label: 'Madagascar' }, { value: 'MW', label: 'Malawi' },
  { value: 'MY', label: 'Malaysia' }, { value: 'MV', label: 'Maldives' }, { value: 'ML', label: 'Mali' },
  { value: 'MT', label: 'Malta' }, { value: 'MH', label: 'Marshall Islands' }, { value: 'MQ', label: 'Martinique' },
  { value: 'MR', label: 'Mauritania' }, { value: 'MU', label: 'Mauritius' }, { value: 'YT', label: 'Mayotte' },
  { value: 'MX', label: 'Mexico' }, { value: 'FM', label: 'Micronesia' }, { value: 'MD', label: 'Moldova' },
  { value: 'MC', label: 'Monaco' }, { value: 'MN', label: 'Mongolia' }, { value: 'ME', label: 'Montenegro' },
  { value: 'MS', label: 'Montserrat' }, { value: 'MA', label: 'Morocco' }, { value: 'MZ', label: 'Mozambique' },
  { value: 'MM', label: 'Myanmar' }, { value: 'NA', label: 'Namibia' }, { value: 'NR', label: 'Nauru' },
  { value: 'NP', label: 'Nepal' }, { value: 'NL', label: 'Netherlands' }, { value: 'NC', label: 'New Caledonia' },
  { value: 'NZ', label: 'New Zealand' }, { value: 'NI', label: 'Nicaragua' }, { value: 'NE', label: 'Niger' },
  { value: 'NG', label: 'Nigeria' }, { value: 'NU', label: 'Niue' }, { value: 'NF', label: 'Norfolk Island' },
  { value: 'MP', label: 'Northern Mariana Islands' }, { value: 'NO', label: 'Norway' }, { value: 'OM', label: 'Oman' },
  { value: 'PK', label: 'Pakistan' }, { value: 'PW', label: 'Palau' }, { value: 'PS', label: 'Palestine' },
  { value: 'PA', label: 'Panama' }, { value: 'PG', label: 'Papua New Guinea' }, { value: 'PY', label: 'Paraguay' },
  { value: 'PE', label: 'Peru' }, { value: 'PH', label: 'Philippines' }, { value: 'PN', label: 'Pitcairn' },
  { value: 'PL', label: 'Poland' }, { value: 'PT', label: 'Portugal' }, { value: 'PR', label: 'Puerto Rico' },
  { value: 'QA', label: 'Qatar' }, { value: 'RE', label: 'Réunion' }, { value: 'RO', label: 'Romania' },
  { value: 'RU', label: 'Russian Federation' }, { value: 'RW', label: 'Rwanda' }, { value: 'BL', label: 'Saint Barthélemy' },
  { value: 'SH', label: 'Saint Helena' }, { value: 'KN', label: 'Saint Kitts and Nevis' }, { value: 'LC', label: 'Saint Lucia' },
  { value: 'MF', label: 'Saint Martin (French part)' }, { value: 'PM', label: 'Saint Pierre and Miquelon' },
  { value: 'VC', label: 'Saint Vincent and the Grenadines' }, { value: 'WS', label: 'Samoa' }, { value: 'SM', label: 'San Marino' },
  { value: 'ST', label: 'Sao Tome and Principe' }, { value: 'SA', label: 'Saudi Arabia' }, { value: 'SN', label: 'Senegal' },
  { value: 'RS', label: 'Serbia' }, { value: 'SC', label: 'Seychelles' }, { value: 'SL', label: 'Sierra Leone' },
  { value: 'SG', label: 'Singapore' }, { value: 'SX', label: 'Sint Maarten (Dutch part)' }, { value: 'SK', label: 'Slovakia' },
  { value: 'SI', label: 'Slovenia' }, { value: 'SB', label: 'Solomon Islands' }, { value: 'SO', label: 'Somalia' },
  { value: 'ZA', label: 'South Africa' }, { value: 'GS', label: 'South Georgia and the South Sandwich Islands' }, { value: 'SS', label: 'South Sudan' },
  { value: 'ES', label: 'Spain' }, { value: 'LK', label: 'Sri Lanka' }, { value: 'SD', label: 'Sudan' },
  { value: 'SR', label: 'Suriname' }, { value: 'SJ', label: 'Svalbard and Jan Mayen' }, { value: 'SZ', label: 'Eswatini' },
  { value: 'SE', label: 'Sweden' }, { value: 'CH', label: 'Switzerland' }, { value: 'SY', label: 'Syrian Arab Republic' },
  { value: 'TW', label: 'Taiwan' }, { value: 'TJ', label: 'Tajikistan' }, { value: 'TZ', label: 'Tanzania' },
  { value: 'TH', label: 'Thailand' }, { value: 'TL', label: 'Timor-Leste' }, { value: 'TG', label: 'Togo' },
  { value: 'TK', label: 'Tokelau' }, { value: 'TO', label: 'Tonga' }, { value: 'TT', label: 'Trinidad and Tobago' },
  { value: 'TN', label: 'Tunisia' }, { value: 'TR', label: 'Turkey' }, { value: 'TM', label: 'Turkmenistan' },
  { value: 'TC', label: 'Turks and Caicos Islands' }, { value: 'TV', label: 'Tuvalu' }, { value: 'UG', label: 'Uganda' },
  { value: 'UA', label: 'Ukraine' }, { value: 'AE', label: 'United Arab Emirates' }, { value: 'GB', label: 'United Kingdom' },
  { value: 'US', label: 'United States' }, { value: 'UM', label: 'United States Minor Outlying Islands' },
  { value: 'UY', label: 'Uruguay' }, { value: 'UZ', label: 'Uzbekistan' }, { value: 'VU', label: 'Vanuatu' },
  { value: 'VE', label: 'Venezuela' }, { value: 'VN', label: 'Vietnam' }, { value: 'VG', label: 'Virgin Islands, British' },
  { value: 'VI', label: 'Virgin Islands, U.S.' }, { value: 'WF', label: 'Wallis and Futuna' }, { value: 'EH', label: 'Western Sahara' },
  { value: 'YE', label: 'Yemen' }, { value: 'ZM', label: 'Zambia' }, { value: 'ZW', label: 'Zimbabwe' }
];

const API_URL = import.meta.env.VITE_API_URL; // For other API calls
const SERVER_API_URL = import.meta.env.VITE_SERVER_API_URL || 'http://localhost:5000'; // Express server URL
const DISCORD_SERVER_URL = import.meta.env.VITE_DISCORD_SERVER_URL || 'https://boxcontents.yyzprep.ca:3004';

// Helper to calculate dropdown position to avoid screen cutoff
const calculateDropdownPosition = (triggerElement, dropdownDimensions) => {
  const rect = triggerElement.getBoundingClientRect();
  const { width: dropdownWidth, height: dropdownHeight } = dropdownDimensions;
  const padding = 8;

  let top = rect.top;
  let left = rect.right + padding;

  // Adjust horizontal position if it overflows right
  if (left + dropdownWidth > window.innerWidth - padding) {
    left = rect.left - dropdownWidth - padding;
  }
  
  // Ensure it doesn't go off-screen left (when switching to left side)
  if (left < padding) {
    left = padding;
  }

  // Adjust vertical position if it overflows bottom
  if (top + dropdownHeight > window.innerHeight - padding) {
    top = window.innerHeight - dropdownHeight - padding;
  }
  
  // Ensure it doesn't go off-screen top
  if (top < padding) {
    top = padding;
  }

  return { top, left };
};
function OutboundItem({ 
  item, 
  quantityInBoxes, 
  availablePrinters = [], 
  loadingPrinters = false,
  autoPrintEnabled = false,
  selectedDefaultPrinter = null,
  // Bulk selection props
  isSelected = false,
  onSelect = () => {}
}) {

  // Move all hooks to the top, before any conditional checks
  const {
    asinData,
    shelvingData,
    updateShelvingQty,
    reloadShipmentData,
    refreshShelving,
    removeShelving,
    servicesData,
    clientInfo,
    reloadAsinData,
    selectedNickname,
    shipmentItems,
    teamId,
    isReloading, // Add this from context
    boxes,
    refreshBoxes, // Add for history functionality
    shipmentId, // Add for history functionality
    fdaClassifications, // Add FDA classifications from context
  } = useContext(BoxContext);

  // Add getItemImageUrl hook at the top with other hooks
  const getItemImageUrl = useCallback(() => {
    if (!item?.item) return '';
    
    // Try to get image from images array first
    if (item.item.images && item.item.images.length > 0) {
      return item.item.images[0].large_url;
    }
    
    // Fallback to direct image URLs
    return (
      item.item.image_url ||
      item.item.main_image_url ||
      'https://qph.cf2.quoracdn.net/main-qimg-654617264f9192ec976abe6e53356240-lq'
    );
  }, [item]);

  // Move currentImageUrl state to the top with other states
  const [currentImageUrl, setCurrentImageUrl] = useState(getItemImageUrl());
  // Single source of truth for expiry date - initialized from props only once
  const [expiryDate, setExpiryDate] = useState(item?.expiry_date ? new Date(item.expiry_date) : null);
  const previousExpiryDateRef = useRef(null); // Track previous value for rollback
  const pendingUpdateRef = useRef(false); // Prevent race conditions
  const updateTimeoutRef = useRef(null); // For debouncing
  const [warning, setWarning] = useState(false);
  const [notification, setNotification] = useState(false);
  const [isUpdatingExpiry, setIsUpdatingExpiry] = useState(false); // Add this state
  const [isUpdatingServices, setIsUpdatingServices] = useState(false); // Add services loading state
  
  // Toast functionality
  const { toast } = useToast();
  
  const dropdownRef = useRef(null);
  const customsModalRef = useRef(null);
  const servicesDropdownRef = useRef(null);
  const [shelvingNotification, setShelvingNotification] = useState(false);
  const [priceNotification, setPriceNotification] = useState(false);
  const [weightNotification, setWeightNotification] = useState(false);
  const [isUpdatingWeight, setIsUpdatingWeight] = useState(false); // Add weight updating state
  const [removedQuantities, setRemovedQuantities] = useState({});
  const totalQuantity = item.quantity;
  const [selectedServices, setSelectedServices] = useState([]);
  const [showTooltip, setShowTooltip] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [isLoadingPrice, setIsLoadingPrice] = useState(false);

  // States for the new print dropdown
  const printDropdownRef = useRef(null);
  const [showPrintDropdown, setShowPrintDropdown] = useState(false);
  const [selectedPrintItem, setSelectedPrintItem] = useState(null);
  const [printQuantity, setPrintQuantity] = useState('custom');
  const [customPrintQty, setCustomPrintQty] = useState('');
  const [useSeparator, setUseSeparator] = useState(false);
  const [separatorInterval, setSeparatorInterval] = useState(10);
  const [extraLabelType, setExtraLabelType] = useState('none');
  const [sequenceMode, setSequenceMode] = useState(false);
  const [printStep, setPrintStep] = useState('config'); // 'config' or 'printer'
  const [printDropdownPosition, setPrintDropdownPosition] = useState({ top: 0, left: 0 });
  const [selectedPrinter, setSelectedPrinter] = useState('');

  // Function to show toast notifications
  const showToast = (message, variant = 'default') => {
    const toastVariant = variant === 'warning' ? 'warning' : 
                        variant === 'error' ? 'error' : 
                        variant === 'success' ? 'success' : 
                        'default';
    
    toast({
      title: message,
      variant: toastVariant
    });
  };

  // Get FDA classification from context instead of fetching individually
  const fdaClassification = fdaClassifications[item?.item?.asin] || null;
  
  // Keep isLoadingFda for consistency, but it's always false since data comes from context
  const isLoadingFda = false;



  // Change the state from an array to an object for proper single SKU updates
  const [prepRequirements, setPrepRequirements] = useState({});
  const [isLoadingPrep, setIsLoadingPrep] = useState(false);
  const [prepError, setPrepError] = useState(null);
  
  // Session cache for prep requirements to avoid repeated API calls
  const [prepCache, setPrepCache] = useState(() => {
    const cached = sessionStorage.getItem('prepRequirementsCache');
    return cached ? JSON.parse(cached) : {};
  });

  const currentYear = new Date().getFullYear();
  const maxYear = currentYear + 10;

  // Calculate preset options dynamically so they don't become stale
  const presetOptions = useMemo(() => {
    const now = new Date();
    return [
      { label: '160 days', value: new Date(now.getFullYear(), now.getMonth(), now.getDate() + 160) },
      { label: '1 year', value: new Date(now.getFullYear() + 1, now.getMonth(), now.getDate() + 1) }, // +1 day
      { label: '2 years', value: new Date(now.getFullYear() + 2, now.getMonth(), now.getDate() + 1) }, // +1 day
      { label: '3 years', value: new Date(now.getFullYear() + 3, now.getMonth(), now.getDate() + 1) }, // +1 day
    ];
  }, []); // Empty deps means it recalculates on every render, keeping it fresh

  const [importInfo, setImportInfo] = useState({
    countryOfOrigin: '',
    countryOfOriginLabel: '', // Add label field
    htsCode: '',
    photos: []
  });

  const [isLoadingImportInfo, setIsLoadingImportInfo] = useState(true);
  const [isLoadingHts, setIsLoadingHts] = useState(false);
  const [htsSuggestions, setHtsSuggestions] = useState(null);

  // Add new state for country save notification
  const [countrySaveNotification, setCountrySaveNotification] = useState(false);

  // Client's suggested country of origin from CLIENT_ASIN_DATA table
  const [clientSuggestedCountry, setClientSuggestedCountry] = useState(null);

  // Add this state for delete confirmation
  const [deletePhotoConfirm, setDeletePhotoConfirm] = useState({ show: false, index: null });

  // Add this state
  const [isHtsEnabled, setIsHtsEnabled] = useState(false);

  // Add these state variables near your other state declarations
  const [isPolling, setIsPolling] = useState(false);
  const [lastImageUpdate, setLastImageUpdate] = useState(null);
  const pollingTimeoutRef = useRef(null);
  const pollStartTimeRef = useRef(null);

  // Add this state to track the last known photo count
  const [lastKnownPhotoCount, setLastKnownPhotoCount] = useState(0);

  // Add this state to track if we're waiting for a response
  const [isProcessing, setIsProcessing] = useState(false);

  // Add this ref to track polling state
  const isPollingRef = useRef(false);

  // Add state for FNSKU sync
  const [isSyncingFnsku, setIsSyncingFnsku] = useState(false);
  const [fnskuSyncMessage, setFnskuSyncMessage] = useState('');

  // Add these state variables inside the OutboundItem component with other state declarations
  const [copiedText, setCopiedText] = useState('');
  const [showCopyConfirmation, setShowCopyConfirmation] = useState(false);

  // Add new state for sequence dialog
  const [showSequenceDialog, setShowSequenceDialog] = useState(false);
  const [showMoreIdentifiers, setShowMoreIdentifiers] = useState(false);
  const [editingWeight, setEditingWeight] = useState(false);
  const [editingPrice, setEditingPrice] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [sequenceCount, setSequenceCount] = useState(4);

  // Add state variables for history functionality
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [historyData, setHistoryData] = useState({ asin: null, title: '', history: [], loading: false });
  const [expandedHistoryIndex, setExpandedHistoryIndex] = useState(null);
  const [historyNotification, setHistoryNotification] = useState({ show: false, message: '' });

  // Add back the shelvingInputValues state
  const [shelvingInputValues, setShelvingInputValues] = useState(
    shelvingData?.reduce((acc, shelving) => {
      acc[shelving.KEY] = shelving.QTY;
      return acc;
    }, {}) || {}
  );

  const [htsError, setHtsError] = useState('');
  // Add a new state for HTS success message
  const [htsSuccess, setHtsSuccess] = useState('');

  // Add missing state variables for the new table format
  const [showCustomsModal, setShowCustomsModal] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');
  const [isAddingShelf, setIsAddingShelf] = useState(false);
  const [newShelfCode, setNewShelfCode] = useState('');
  const [newShelfQuantity, setNewShelfQuantity] = useState('');
  const [selectedPhotoPreview, setSelectedPhotoPreview] = useState({ show: false, photo: null, index: null });
  const [showPrinterDialog, setShowPrinterDialog] = useState(false);
  const [pendingPrintJob, setPendingPrintJob] = useState(null);

  // Add this function with other function declarations
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedText(label);
      setShowCopyConfirmation(true);
      setTimeout(() => {
        setShowCopyConfirmation(false);
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy text: ', err);
    });
  };

  // Sort servicesData alphabetically by service name and create options
  const sortedServices = servicesData?.services ? [...servicesData.services].sort((a, b) => a.name.localeCompare(b.name)) : [];
  const serviceOptions = sortedServices.map(service => ({
    value: service.name,
    label: service.name,
    ITEM_ID: service.id, // Include the ITEM_ID
    service: service // Include the full service object
  }));
  const filteredServiceOptions = serviceOptions.filter(service =>
    service.label.toLowerCase().includes(serviceSearchTerm.toLowerCase())
  );

  // Add alias for photo removal function
  const handlePhotoRemove = (index) => handlePhotoDelete(index);

  // Function to extract UPCs and EANs from identifiers
  const getUPCsAndEANs = () => {
    if (!item?.item?.identifiers) return { upcs: [], eans: [] };
    
    const upcs = item.item.identifiers
      .filter(id => id.identifier_type === 'UPC')
      .map(id => id.identifier);
    
    const eans = item.item.identifiers
      .filter(id => id.identifier_type === 'EAN')
      .map(id => id.identifier);
    
    return { upcs, eans };
  };

  // Function to highlight search terms in text
  const highlightText = (text, searchTerm, shouldHighlight = false) => {
    if (!shouldHighlight || !searchTerm || !text) {
      return <span>{text}</span>;
    }

    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return (
      <span>
        {parts.map((part, index) =>
          regex.test(part) ? (
            <mark key={index} className="bg-yellow-200 px-1 rounded font-medium">
              {part}
            </mark>
          ) : (
            <span key={index}>{part}</span>
          )
        )}
      </span>
    );
  };

  // Function to highlight text with smart truncation
  const highlightTextWithTruncation = (text, searchTerm, shouldHighlight = false, maxLength = 100) => {
    if (!text) return <span></span>;
    
    if (!shouldHighlight || !searchTerm) {
      return <span>{truncateText(text, maxLength)}</span>;
    }

    // If highlighting is needed, show more context around the match
    const lowerText = text.toLowerCase();
    const lowerSearchTerm = searchTerm.toLowerCase();
    const matchIndex = lowerText.indexOf(lowerSearchTerm);
    
    if (matchIndex === -1) {
      return <span>{truncateText(text, maxLength)}</span>;
    }

    // If the text is short enough or match is near the beginning, show the full highlighted text
    if (text.length <= maxLength || matchIndex < maxLength * 0.6) {
      return highlightText(text, searchTerm, true);
    }

    // For longer text with matches later in the string, show context around the match
    const contextStart = Math.max(0, matchIndex - Math.floor(maxLength * 0.3));
    const contextEnd = Math.min(text.length, contextStart + maxLength);
    const contextText = text.substring(contextStart, contextEnd);
    const prefix = contextStart > 0 ? '...' : '';
    const suffix = contextEnd < text.length ? '...' : '';
    
    return (
      <span>
        {prefix}
        {highlightText(contextText, searchTerm, true)}
        {suffix}
      </span>
    );
  };

  // All useEffect hooks must be here, before any conditional returns
  useEffect(() => {
    setCurrentImageUrl(getItemImageUrl());
  }, [item, getItemImageUrl]);

  // Only sync expiry date from props if we're not in the middle of an update
  // This prevents fighting between local optimistic updates and server data
  useEffect(() => {
    if (!pendingUpdateRef.current && !isUpdatingExpiry) {
      const newDate = item?.expiry_date ? new Date(item.expiry_date) : null;
      setExpiryDate(newDate);
    }
  }, [item?.expiry_date, isUpdatingExpiry]);

  useEffect(() => {
    // Pre-select existing services
    const preSelectedServices = item?.service_lines?.map(serviceLine => ({
      value: serviceLine.service.name,
      label: serviceLine.service.name,
      ITEM_ID: serviceLine.shipment_item_id,
      service: serviceLine.service
    })) || [];
    setSelectedServices(preSelectedServices);
  }, [item]);

  useEffect(() => {
    setShelvingInputValues(
      shelvingData?.reduce((acc, shelving) => {
        acc[shelving.KEY] = shelving.QTY;
        return acc;
      }, {}) || {}
    );
  }, [shelvingData]);

  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      setIsPolling(false);
      isPollingRef.current = false;
    };
  }, []);

  // Auto-expand identifiers if UPC or EAN matches are found
  useEffect(() => {
    if (item?._searchMatches && (item._searchMatches.upcs?.length > 0 || item._searchMatches.eans?.length > 0)) {
      setShowMoreIdentifiers(true);
    }
  }, [item?._searchMatches]);

  // Load saved printer preference when printers are available
  useEffect(() => {
    if (availablePrinters.length > 0) {
      const savedPrinter = localStorage.getItem('selectedPrinter');
      if (savedPrinter && availablePrinters.some(p => p.id === savedPrinter)) {
        setSelectedPrinter(savedPrinter);
      } else {
        setSelectedPrinter(availablePrinters[0].id);
      }
    }
  }, [availablePrinters]);

  const handlePriceCheck = async (asin) => {
    try {
      setIsLoadingPrice(true);
      // Fetch the price
      const response = await fetch(`${API_URL}/api/price?asin=${asin}`);
      const data = await response.json();
      
      if (data.price && data.price !== -1) { // Only update if we got a valid price
        // Update the local state
        setPrice(data.price);
        
        // Update the database
        const updateResponse = await fetch(`${API_URL}/api/update_price`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            asin: asin, 
            value: data.price,
            employee: selectedNickname
          })
        });

        if (!updateResponse.ok) {
          throw new Error('Failed to update price in database');
        }

        // Show success notification
        setPriceNotification(true);
        reloadAsinData();

        // Hide the notification after 0.5 seconds
        setTimeout(() => {
          setPriceNotification(false);
        }, 500);
      } else {
        console.warn('Invalid price received:', data);
        // Optionally show an error message to the user
        alert('Unable to fetch a valid price');
      }
    } catch (error) {
      console.error('Error fetching/updating price:', error);
      alert('Error updating price');
    } finally {
      setIsLoadingPrice(false);
    }
  };

  const formatCurrency = (value) => {
    return value;
  };

  const getAsinData = (asin) => {
    return asinData.find(data => data.ASIN === asin) || {};
  };

  const asinDataItem = getAsinData(item.item.asin);
  const [price, setPrice] = useState(asinDataItem.VALUE || ''); // Initialize price after asinDataItem is defined
  const [weight, setWeight] = useState(
    asinDataItem.WEIGHT && !isNaN(parseFloat(asinDataItem.WEIGHT)) ? parseFloat(asinDataItem.WEIGHT).toFixed(2) : ''
  ); // Initialize weight after asinDataItem is defined with 2 decimal formatting

  // Update price when asinDataItem changes
  useEffect(() => {
    setPrice(asinDataItem.VALUE || '');
  }, [asinDataItem.VALUE]);

  // Update weight when asinDataItem changes
  useEffect(() => {
    const newWeight = asinDataItem.WEIGHT && !isNaN(parseFloat(asinDataItem.WEIGHT)) ? parseFloat(asinDataItem.WEIGHT).toFixed(2) : '';
    console.log('🔧 [OutboundItem] Weight effect triggered:', { 
      asin: item.item.asin, 
      oldWeight: weight, 
      newWeight, 
      asinDataWeight: asinDataItem.WEIGHT 
    });
    setWeight(newWeight);
  }, [asinDataItem.WEIGHT]);

  const formatDate = (date) => {
    // Add one day to compensate for server timezone shift
    const adjustedDate = new Date(date);
    adjustedDate.setDate(adjustedDate.getDate() + 1);
    adjustedDate.setHours(12, 0, 0, 0);
    return adjustedDate.toISOString();
  };

  const customPrintDropdownRef = useRef(null); // Ref for custom print dropdown

  // Handle clicks outside the dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (printDropdownRef.current && !printDropdownRef.current.contains(event.target)) {
        setShowPrintDropdown(false);
      }
      if (customsModalRef.current && !customsModalRef.current.contains(event.target)) {
        setShowCustomsModal(false);
      }
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target)) {
        setShowServicesDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef, customsModalRef, servicesDropdownRef, printDropdownRef]);

  const updateExpiryDate = useCallback(async (date) => {
    // STEP 1: VALIDATE BEFORE ANY STATE CHANGES
    if (date) {
      const currentDate = new Date();
      const differenceInDays = (date - currentDate) / (1000 * 60 * 60 * 24);

      // Check if client name contains 'openmarket' (case insensitive)
      const isOpenMarketplace = clientInfo?.name?.toLowerCase().includes('openmarket');

      // Validation: OpenMarketplace requirement
      if (isOpenMarketplace && differenceInDays <= 365) {
        setWarning(true);
        showToast('For OpenMarketplace, expiry date must be more than 365 days from today.', 'warning');
        return; // Don't proceed with the update
      }

      // Validation: Forbidden date check (Jan 1, 2030)
      if (isForbiddenExpiryDate(date)) {
        showToast('Cannot use temporary expiration date (Jan 1, 2030). Please set a proper expiration date or use a preset (1y, 2y, 3y).', 'warning');
        return; // Don't proceed
      }

      // Set warning for dates < 90 days (non-blocking)
      if (!isOpenMarketplace && differenceInDays < 90) {
        setWarning(true);
      } else {
        setWarning(false);
      }
    } else {
      setWarning(false);
    }

    // STEP 2: PREVENT RACE CONDITIONS
    if (pendingUpdateRef.current) {
      console.log('Update already in progress, skipping...');
      return;
    }

    // STEP 3: SAVE PREVIOUS STATE FOR ROLLBACK
    previousExpiryDateRef.current = expiryDate;
    pendingUpdateRef.current = true;

    // STEP 4: OPTIMISTIC UPDATE - Update UI immediately
    setExpiryDate(date);

    const newExpiryDate = date ? formatDate(date) : null;
    const itemId = item.id;
    const shipmentId = item.shipment_id;

    try {
      setIsUpdatingExpiry(true);
      console.log("ITEM ID WE'RE SENDING PATCH TO: ", itemId);
      const modifiedItem = {
        ...item,
        expiry_date: newExpiryDate
      };
      const response = await fetch(`${API_URL}/api/update-expiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          itemId: itemId,
          shipmentId: shipmentId,
          item: modifiedItem,
        }),
      });

      if (response.ok) {
        console.log('Expiry date updated successfully');
        console.log(newExpiryDate);
        showToast('Expiry date updated successfully', 'success');

        // Reload shipment data to ensure all components have updated data
        await reloadShipmentData();
      } else {
        // STEP 5: ROLLBACK ON FAILURE
        console.error('Failed to update expiry date');
        setExpiryDate(previousExpiryDateRef.current);
        showToast('Failed to update expiry date', 'error');
      }
    } catch (error) {
      // STEP 5: ROLLBACK ON ERROR
      console.error('Error updating expiry date:', error);
      setExpiryDate(previousExpiryDateRef.current);
      showToast('Error updating expiry date', 'error');
    } finally {
      setIsUpdatingExpiry(false);
      pendingUpdateRef.current = false;
      previousExpiryDateRef.current = null;
    }
  }, [expiryDate, clientInfo, item.id, item.shipment_id, showToast, reloadShipmentData]);

  // Debounced version of updateExpiryDate to prevent rapid-fire updates
  const debouncedUpdateExpiryDate = useCallback((date) => {
    // Clear any pending timeout
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    // Set new timeout for 300ms
    updateTimeoutRef.current = setTimeout(() => {
      updateExpiryDate(date);
    }, 300);
  }, [updateExpiryDate]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  // Handler for when date picker completes a selection (onBlur callback)
  const handleExpiryDateBlur = useCallback((dateString) => {
    // Check if value actually changed
    const currentDateString = expiryDate ? expiryDate.toISOString().split('T')[0] : '';
    if (dateString === currentDateString) {
      console.log('Date unchanged, skipping update');
      return; // No change, don't update
    }

    if (!dateString) {
      updateExpiryDate(null);
      return;
    }

    // Parse the date string
    const parsedDate = new Date(dateString);
    if (isNaN(parsedDate.getTime())) {
      console.warn('Invalid date string:', dateString);
      return;
    }

    // Update immediately (presets and calendar selections are final)
    updateExpiryDate(parsedDate);
  }, [updateExpiryDate, expiryDate]);

  // Handler for display-only changes (typing in the input)
  const handleExpiryDateChange = useCallback((dateString) => {
    // Only update the display, don't save yet
    if (!dateString) {
      setExpiryDate(null);
      return;
    }

    const parsedDate = new Date(dateString);
    if (!isNaN(parsedDate.getTime())) {
      setExpiryDate(parsedDate);
    }
  }, []);

  const handlePriceBlur = async (e) => {
    console.log('Price blur event:', e.target.value);
    const newValue = e.target.value;
    setPrice(newValue);
    console.log('New price:', newValue);

    try {
      const asinIdentifier = item.item.identifiers.find(id => id.identifier_type === 'ASIN').identifier;
      const response = await fetch(`${API_URL}/api/update_price`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ asin: asinIdentifier, value: newValue })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to update price');
      }

      console.log('Price updated successfully:', result.message);
      setPriceNotification(true); // Show notification
      reloadAsinData();

      // Hide the notification after 0.5 seconds
      setTimeout(() => {
        setPriceNotification(false);
      }, 500);
    } catch (error) {
      console.error('Error updating price:', error.message);
    }
  };

  const handleWeightBlur = async (e) => {
    const newValue = e.target.value;
    console.log('🔧 [OutboundItem] Weight blur:', { asin: item.item.asin, newValue, currentWeight: weight });

    try {
      setIsUpdatingWeight(true);
      const asinIdentifier = item.item.identifiers.find(id => id.identifier_type === 'ASIN').identifier;
      
      console.log('🔧 [OutboundItem] Calling API with:', { asin: asinIdentifier, weight: newValue, employee: selectedNickname });
      
      const response = await fetch(`${API_URL}/api/update_weight`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          asin: asinIdentifier, 
          weight: newValue,
          employee: selectedNickname
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to update weight');
      }

      console.log('🔧 [OutboundItem] API success, starting data refresh...');
      setWeightNotification(true);
      
      // Wait for asin data refresh to complete  
      if (reloadAsinData) {
        await reloadAsinData();
        console.log('🔧 [OutboundItem] Data refresh completed');
      }
      
      // Force update local weight state with the new value
      const formattedWeight = newValue && !isNaN(parseFloat(newValue)) ? parseFloat(newValue).toFixed(2) : '';
      setWeight(formattedWeight);
      console.log('🔧 [OutboundItem] Local state updated to:', formattedWeight);
      
      showToast(`Weight updated to ${newValue}kg`, 'success');

      // Hide the notification after 0.5 seconds
      setTimeout(() => {
        setWeightNotification(false);
      }, 500);
    } catch (error) {
      console.error('🔧 [OutboundItem] Error updating weight:', error.message);
      showToast('Failed to update weight', 'error');
    } finally {
      setIsUpdatingWeight(false);
    }
  };

  const handleShelvingQtyChange = (key, value) => {
    setShelvingInputValues(prevState => ({
      ...prevState,
      [key]: value,
    }));
  };

  const handleShelvingQtyBlur = async (key, shelfCode, e) => {
    const newQty = e.target.value;
    setShelvingInputValues(prevState => ({
      ...prevState,
      [key]: newQty,
    }));
    try {
      await updateShelvingQty(key, shelfCode, newQty, item.item.merchant_sku);
      setShelvingNotification(true); // Show notification

      // Hide the notification after 0.5 seconds
      setTimeout(() => {
        setShelvingNotification(false);
      }, 500);
    } catch (error) {
      console.error('Error updating shelving quantity:', error);
      alert('Error updating shelving quantity');
    }
  };

  const handleRemoveBoxedFromShelf = async (shelfKey, shelfCode) => {
    const currentQtyOnShelf = parseInt(shelvingInputValues[shelfKey] || 0);
    const boxedQty = displayQuantityInBoxes;
    
    if (boxedQty <= 0) {
        showToast('No boxed quantity to remove.', 'warning');
        return;
    }

    const newQty = Math.max(0, currentQtyOnShelf - boxedQty);

    setShelvingInputValues(prevState => ({
      ...prevState,
      [shelfKey]: newQty.toString(),
    }));

    try {
      await updateShelvingQty(shelfKey, shelfCode, newQty, item.item.merchant_sku);
      setShelvingNotification(true);
      showToast(`${boxedQty} item(s) removed from shelf ${shelfCode}. New quantity: ${newQty}.`, 'success');

      setTimeout(() => {
        setShelvingNotification(false);
      }, 500);
    } catch (error) {
      console.error('Error updating shelving quantity:', error);
      showToast('Error updating shelving quantity', 'error');
    }
  };

  let remainingQty = totalQuantity - quantityInBoxes; 

  // Function to handle the removal of specific quantities from the shelves
  // Function to validate shelf code format (X-XX-XX or similar with dashes)
  const validateShelfCode = (code) => {
    if (!code) return false;
    // Must contain at least one dash and have characters between dashes
    const parts = code.split('-');
    return parts.length >= 2 && parts.every(part => part.length > 0);
  };

  const handleAddShelf = async () => {
    if (!newShelfCode || !newShelfQuantity) {
      showToast('Please enter both shelf code and quantity', 'error');
      return;
    }
    
    // Auto-caps and validate shelf code
    const formattedShelfCode = newShelfCode.toUpperCase();
    
    if (!validateShelfCode(formattedShelfCode)) {
      showToast('Shelf code must follow format X-XX-XX (with dashes between sections)', 'error');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/add_or_update_shelving_qty`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          key: `${formattedShelfCode}-${item.item.merchant_sku}`,
          qty: parseInt(newShelfQuantity),
          sku: item.item.merchant_sku,
          shelfCode: formattedShelfCode,
          employee: selectedNickname,
          shipmentId: item.shipment_id || 'N/A',
          action_type: 'BOX_CONTENTS_ADD_SHELF',
          teamId: teamId || clientInfo?.id || null
        }),
      });

      if (response.ok) {
        // Reset form immediately
        setIsAddingShelf(false);
        setNewShelfCode('');
        setNewShelfQuantity('');
        
        // Force refresh of shelving data only (faster than full reload)
        await refreshShelving();
        
        // Show success notification
        showToast('Shelf added successfully', 'success');
      } else {
        const errorData = await response.json();
        showToast(`Failed to add shelf: ${errorData.error || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Error adding shelf:', error);
      showToast('Error adding shelf', 'error');
    }
  };

  const handleRemoveShelf = async (shelfKey, shelfCode) => {
    try {
      await removeShelving(shelfKey, shelfCode, item.item.merchant_sku);
      setShelvingNotification(true);
      
      // Hide the notification after 0.5 seconds
      setTimeout(() => {
        setShelvingNotification(false);
      }, 500);
    } catch (error) {
      console.error('Error removing shelf:', error);
      showToast('Error removing shelf', 'error');
    }
  };

  const handleRemoveFromShelf = async (shelfKey, shelfCode, quantityToRemove) => {
    const currentQty = shelvingInputValues[shelfKey];
    const newQty = currentQty - quantityToRemove;
  
    if (newQty < 0) {
      alert('Cannot remove more than the available quantity on the shelf.');
      return;
    }
  
    setShelvingInputValues(prevState => ({
      ...prevState,
      [shelfKey]: newQty,
    }));
  
    setRemovedQuantities(prevState => ({
      ...prevState,
      [shelfKey]: (prevState[shelfKey] || 0) + quantityToRemove,
    }));
  
    try {
      await updateShelvingQty(shelfKey, shelfCode, newQty, item.item.merchant_sku);
      setShelvingNotification(true); // Show notification
  
      // Hide the notification after 0.5 seconds
      setTimeout(() => {
        setShelvingNotification(false);
      }, 500);
    } catch (error) {
      console.error('Error updating shelving quantity:', error);
      alert('Error updating shelving quantity');
    }
  };
  
  const updateServices = async (selectedServices) => {
    const servicesPayload = selectedServices.reduce((acc, service, index) => {
      acc[index] = {
        id: service.service.id,
        name: service.service.name,
        charge: service.service.charge,
        advanced_options: service.service.advanced_options,
        quantity: 1 // assuming quantity is always 1
      };
      return acc;
    }, {});

    try {
      setIsUpdatingServices(true);
      const response = await fetch(`https://app.yyzprep.ca/api/shipments/outbound/${item.shipment_id}/outbound-shipment-item/${item.id}/services?api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          services: servicesPayload
        }),
      });

      if (response.ok) {
        console.log('Services updated successfully');
        showToast('Services updated successfully', 'success');
        
        // We still need to reload shipment data for other UI elements to update,
        // but we'll add a small delay to ensure prep requirements fetch completes first
        await reloadShipmentData();
        
        // Additional logging to help debug oversized fee issues
        console.log('Service update completed for item:', item.item.merchant_sku);
        console.log('Updated services payload:', servicesPayload);
      } else {
        console.error('Failed to update services');
        showToast('Failed to update services', 'error');
      }
    } catch (error) {
      console.error('Error updating services:', error);
      showToast('Error updating services', 'error');
    } finally {
      setIsUpdatingServices(false);
    }
  };

  const handleServiceChange = async (selectedOptions) => {
    try {
      setSelectedServices(selectedOptions || []);
      await updateServices(selectedOptions || []);
      
      // Only fetch prep requirements for this specific SKU after services are updated
      fetchPrepRequirementsForSku(item.item.merchant_sku);
    } catch (error) {
      console.error('Error in handleServiceChange:', error);
    }
  };

  // Add a function to fetch prep requirements for a specific SKU with caching
  const fetchPrepRequirementsForSku = async (sku) => {
    if (!sku) return;
    
    // Check if we already have this SKU's prep requirements cached
    if (prepCache[sku]) {
      console.log('Using cached prep requirements for SKU:', sku);
      setPrepRequirements(prev => ({
        ...prev,
        [sku]: prepCache[sku]
      }));
      return;
    }
    
    try {
      setIsLoadingPrep(true);
      setPrepError(null);

      const queryParams = new URLSearchParams();
      queryParams.append('teamId', clientInfo.id);
      queryParams.append('mskus[]', sku);

      const response = await fetch(`${SERVER_API_URL}/api/item-prep-details?${queryParams.toString()}`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch prep requirements');
      }

      const data = await response.json();
      console.log('Fetched prep requirements for single SKU:', data.prepRequirements);
      
      // Update both the state and cache
      const skuPrepReq = data.prepRequirements[sku];
      setPrepRequirements(prev => ({
        ...prev,
        [sku]: skuPrepReq
      }));
      
      // Update cache and sessionStorage
      const newCache = { ...prepCache, [sku]: skuPrepReq };
      setPrepCache(newCache);
      sessionStorage.setItem('prepRequirementsCache', JSON.stringify(newCache));
      
    } catch (error) {
      console.error('Error fetching prep requirements:', error.message);
      setPrepError('Failed to fetch prep requirements.');
    } finally {
      setIsLoadingPrep(false);
    }
  };

  const checkOversize = (item) => {
    const lengthInches = item.length_mm / 25.4;
    const widthInches = item.width_mm / 25.4;
    const heightInches = item.height_mm / 25.4;
    const weightLbs = item.weight_gm / 453.592;

    const isOversized = lengthInches > 18 || widthInches > 14 || heightInches > 8 || weightLbs > 20;
    const isHugeOversized = isOversized && (lengthInches >= 25 || widthInches >= 25 || heightInches >= 25);

    if (isHugeOversized) return 'Huge Oversized';
    if (isOversized) return 'Oversized';
    return '';
  };

  const oversizeTag = checkOversize(item.item);

  let statusBadge = null;
  if (quantityInBoxes === 0) {
    statusBadge = (
      <span className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-bl-lg">
        Not Started
      </span>
    );
  } else if (quantityInBoxes < totalQuantity) {
    statusBadge = (
      <span className="absolute top-0 right-0 bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded-bl-lg">
        In Progress
      </span>
    );
  } else if (quantityInBoxes === totalQuantity) {
    statusBadge = (
      <span className="absolute top-0 right-0 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-bl-lg">
        Completed
      </span>
    );
  } else if (quantityInBoxes > totalQuantity) {
    statusBadge = (
      <span className="absolute top-0 right-0 bg-red-700 text-white text-xs font-bold px-2 py-1 rounded-bl-lg">
        Over Limit
      </span>
    );
  }



  const formatDimensions = (length, width, height) => {
    return `${Math.round(length / 10)} x ${Math.round(width / 10)} x ${Math.round(height / 10)} cm`;
  };

  const formatWeight = (weight) => {
    return `${(weight / 1000).toFixed(2)} kg`;
  };

  const handleMouseEnter = () => {
    const timeout = setTimeout(() => {
      setShowTooltip(true);
    }, 250); // 0.25 second delay
    setHoverTimeout(timeout);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeout);
    setShowTooltip(false);
  };

  const handlePrintOptionChange = (quantity, option, setShowPrintOptions) => {
    setPrintOption(option);
    setShowPrintOptions(false);
    handlePrint(quantity, option);
  };
  
  // Helper function to check if expiration date is the forbidden placeholder
  const isForbiddenExpiryDate = (date) => {
    if (!date) return false;
    const checkDate = new Date(date);
    const forbiddenDate = new Date('2030-01-01');
    return checkDate.getFullYear() === forbiddenDate.getFullYear() && 
           checkDate.getMonth() === forbiddenDate.getMonth() && 
           checkDate.getDate() === forbiddenDate.getDate();
  };

  const handlePrint = (quantity, option) => {
    // Check for US client and missing country of origin
    if (clientInfo && clientInfo.country === 'USA' && !importInfo.countryOfOrigin) {
      showToast('Please select the Country of Origin for this US shipment before printing labels.', 'warning');
      return;
    }

    // Check for forbidden expiration date (Jan 1, 2030 placeholder)
    if (isForbiddenExpiryDate(expiryDate)) {
      showToast('Cannot print labels with temporary expiration date (Jan 1, 2030). Please set a proper expiration date or use a preset (1y, 2y, 3y).', 'warning');
      return;
    }

    const autoPrintEnabled = localStorage.getItem('autoPrintEnabled') === 'true';
    
    if (autoPrintEnabled) {
      // Check if we have printers available
      if (availablePrinters.length === 0) {
        showToast('No printers available. Please check your printer settings.', 'warning');
        return;
      }
      
      // Show printer selection dialog using already fetched printers
      setPendingPrintJob({ quantity, option });
      setShowPrinterDialog(true);
    } else {
      // Proceed with normal printing (no auto print)
      executePrePrint(quantity, option, null);
    }
  };
  
  const truncateText = (text, maxLength, firstPercentage = 60, lastPercentage = 40) => {
    if (text.length <= maxLength) return text; // Return the full text if it's shorter than maxLength
  
    const firstChars = Math.floor((firstPercentage / 100) * text.length); // First X% of characters
    const lastChars = Math.floor((lastPercentage / 100) * text.length); // Last Y% of characters
  
    const firstPart = text.slice(0, firstChars); // First X% of characters
    const lastPart = text.slice(-lastChars); // Last Y% of characters
  
    return `${firstPart}...${lastPart}`;
  };

  const togglePrintOptions = (setShowPrintOptions) => {
    setShowPrintOptions(prev => !prev);
  };

  useEffect(() => {
    const fetchPrepRequirements = async () => {
      // Extract all MSKUs from the shipment
      const mskus = shipmentItems.map(shipmentItem => shipmentItem.item.merchant_sku);
      
      if (mskus.length === 0) {
        setPrepError('No MSKUs found in the shipment.');
        return;
      }

      // Check which MSKUs are not in cache
      const uncachedMskus = mskus.filter(msku => !prepCache[msku]);
      
      // If all MSKUs are cached, use cached data
      if (uncachedMskus.length === 0) {
        console.log('Using cached prep requirements for all MSKUs');
        const cachedRequirements = {};
        mskus.forEach(msku => {
          if (prepCache[msku]) {
            cachedRequirements[msku] = prepCache[msku];
          }
        });
        setPrepRequirements(cachedRequirements);
        return;
      }

      try {
        setIsLoadingPrep(true);
        setPrepError(null); // Reset any previous errors

        const queryParams = new URLSearchParams();
        queryParams.append('teamId', clientInfo.id);
        uncachedMskus.forEach(msku => queryParams.append('mskus[]', msku));

        const response = await fetch(`${SERVER_API_URL}/api/item-prep-details?${queryParams.toString()}`);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch prep requirements');
        }

        const data = await response.json();

        // Debugging: Log the fetched prep requirements
        console.log('Fetched prepRequirements for uncached MSKUs:', data.prepRequirements);

        // Merge cached and new data
        const allRequirements = { ...prepRequirements };
        mskus.forEach(msku => {
          if (prepCache[msku]) {
            allRequirements[msku] = prepCache[msku];
          } else if (data.prepRequirements[msku]) {
            allRequirements[msku] = data.prepRequirements[msku];
          }
        });

        setPrepRequirements(allRequirements);
        
        // Update cache with new data
        const newCache = { ...prepCache, ...data.prepRequirements };
        setPrepCache(newCache);
        sessionStorage.setItem('prepRequirementsCache', JSON.stringify(newCache));
        
      } catch (error) {
        console.error('Error fetching prep requirements:', error.message);
        setPrepError('Failed to fetch prep requirements.');
      } finally {
        setIsLoadingPrep(false);
      }
    };

    fetchPrepRequirements();
  }, [shipmentItems, clientInfo.id]); // Removed prepCache from dependencies to prevent unnecessary re-fetches

  // Modify handleCountryChange
  const handleCountryChange = async (selectedOption) => {
    const newCountryCode = selectedOption ? selectedOption.value : '';
    const newCountryLabel = selectedOption ? selectedOption.label : '';
    
    // Update local state immediately for instant UI feedback
    setImportInfo(prev => ({
      ...prev,
      countryOfOrigin: newCountryCode,
      countryOfOriginLabel: newCountryLabel
    }));
    
    try {
      const response = await fetch(`${SERVER_API_URL}/api/update-usa-import-info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          asin: item.item.asin,
          field: 'country_of_origin',
          value: newCountryCode, // Send the code to the backend
          updatedBy: selectedNickname
        })
      });

      if (response.ok) {
        // Show save notification
        setCountrySaveNotification(true);
        
        // Reload shipment data to ensure all components have updated data
        if (reloadShipmentData) {
          await reloadShipmentData();
        }
        
        setTimeout(() => {
          setCountrySaveNotification(false);
        }, 2000);
      } else {
        // If save failed, revert local state
        setImportInfo(prev => ({
          ...prev,
          countryOfOrigin: prev.countryOfOrigin || '',
          countryOfOriginLabel: prev.countryOfOriginLabel || ''
        }));
        showToast('Failed to save country of origin', 'error');
      }

    } catch (error) {
      console.error('Error updating country of origin:', error);
      // If error occurred, revert local state
      setImportInfo(prev => ({
        ...prev,
        countryOfOrigin: prev.countryOfOrigin || '',
        countryOfOriginLabel: prev.countryOfOriginLabel || ''
      }));
      showToast('Error updating country of origin', 'error');
    }
  };

  const isValidHtsCode = (htsCode) => {
    return /^\d{6}$|^\d{8}$|^\d{10}$/.test(htsCode);
  };

  const handleHtsChange = (newHtsCode) => {
    setImportInfo(prev => ({ ...prev, htsCode: newHtsCode })); // Always update the state

    if (!isValidHtsCode(newHtsCode)) {
      setHtsError('Invalid HTS code. It must be exactly 6, 8, or 10 digits long.');
      setHtsSuccess(''); // Clear success message when invalid
    } else {
      setHtsError(''); // Clear error if valid
      setHtsSuccess('Valid HTS code.'); // Set success message
    }

    // Save to database if valid
    if (isValidHtsCode(newHtsCode)) {
      fetch(`${SERVER_API_URL}/api/update-usa-import-info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          asin: item.item.asin,
          field: 'hts_code',
          value: newHtsCode,
          updatedBy: selectedNickname
        })
      }).then(() => {
        // Show save notification
        setCountrySaveNotification(true);
        setTimeout(() => {
          setCountrySaveNotification(false);
        }, 2000);
      }).catch(error => {
        console.error('Error saving HTS code:', error);
      });
    }
  };

  // Add useEffect to validate HTS code on initial load
  useEffect(() => {
    if (importInfo.htsCode) {
      if (!isValidHtsCode(importInfo.htsCode)) {
        setHtsError('Invalid HTS code. It must be exactly 6, 8, or 10 digits long.');
        setHtsSuccess('');
      } else {
        setHtsError('');
        setHtsSuccess('Valid HTS code.');
      }
    } else {
      setHtsError('');
      setHtsSuccess('');
    }
  }, [importInfo.htsCode]);

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handlePhotoAdd = async (file) => {
    try {
      const formData = new FormData();
      formData.append('photo', file);
      formData.append('asin', item.item.asin);
      formData.append('employee', selectedNickname);

      const response = await fetch(`${API_URL}/api/upload_origin_photo`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to upload photo');
      }

      const data = await response.json();
      
      // Update local state with the new photo
      setImportInfo(prevInfo => ({
        ...prevInfo,
        photos: [...(prevInfo.photos || []), {
          url: data.url,
          timestamp: new Date().toISOString()
        }]
      }));

      // Update database with the new photos array
      const updateResponse = await fetch(`${API_URL}/api/update_origin_photos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          asin: item.item.asin,
          photos: [...(importInfo.photos || []), {
            url: data.url,
            timestamp: new Date().toISOString()
          }],
          employee: selectedNickname
        })
      });

      if (!updateResponse.ok) {
        throw new Error('Failed to update photos in database');
      }

    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo');
    }
  };

  const handlePhotoDelete = async (index) => {
    try {
      const photoToDelete = importInfo.photos[index];
      if (!photoToDelete) {
        console.error('Photo not found');
        return;
      }

      // Check if we have a public_id (Cloudinary photo)
      if (photoToDelete.public_id) {
        // Delete from Cloudinary
        const response = await fetch(`${SERVER_API_URL}/api/delete-origin-proof`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            public_id: photoToDelete.public_id 
          })
        });

        if (!response.ok) {
          throw new Error('Failed to delete from Cloudinary');
        }
      }

      // Update local state
      const updatedPhotos = importInfo.photos.filter((_, i) => i !== index);
      setImportInfo(prev => ({
        ...prev,
        photos: updatedPhotos
      }));

      // Update database
      await fetch(`${SERVER_API_URL}/api/update-usa-import-info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          asin: item.item.asin,
          field: 'origin_photos',
          value: updatedPhotos,
          updatedBy: selectedNickname
        })
      });

    } catch (error) {
      console.error('Error deleting photo:', error);
    } finally {
      // Close the confirmation dialog
      setDeletePhotoConfirm({ show: false, index: null });
    }
  };

  useEffect(() => {
    const fetchImportInfo = async () => {
      if (clientInfo.country === 'USA' && item.item.asin) {
        setIsLoadingImportInfo(true);
        try {
          const response = await fetch(`${SERVER_API_URL}/api/usa-import-info/${item.item.asin}`);
          if (!response.ok) {
            throw new Error('Failed to fetch import info');
          }
          
          const data = await response.json();
          if (data) {
            console.log('Loaded import info:', data);
            const countryCode = data.country_of_origin || '';
            const countryObject = countries.find(c => c.value === countryCode);
            const countryLabel = countryObject ? countryObject.label : '';
            
            setImportInfo(prev => ({ 
              ...prev,
              countryOfOrigin: countryCode,
              countryOfOriginLabel: countryLabel, // Set the label based on the loaded code
              htsCode: data.hts_code || '',
              photos: Array.isArray(data.origin_photos) ? data.origin_photos : [],
              last_updated: data.last_updated || null,
              created_at: data.created_at || null,
              updated_by: data.updated_by || null
            }));
          }
        } catch (error) {
          console.error('Error fetching import info:', error);
        } finally {
          setIsLoadingImportInfo(false);
        }
      } else {
        setIsLoadingImportInfo(false);
      }
    };

    fetchImportInfo();
  }, [item.item.asin, clientInfo.country]); // Dependencies array

  // Fetch client's suggested country of origin from CLIENT_ASIN_DATA
  useEffect(() => {
    const fetchClientAsinData = async () => {
      // Only fetch for US clients and when we have a valid ASIN and client ID
      if (clientInfo.country === 'USA' && item.item.asin && (teamId || clientInfo.id)) {
        try {
          const clientId = teamId || clientInfo.id;
          const response = await fetch(`${SERVER_API_URL}/api/client-asin-data/${item.item.asin}?clientId=${clientId}`);

          if (response.ok) {
            const data = await response.json();
            if (data && data.country_of_origin) {
              // Look up the country label
              const countryObject = countries.find(c => c.value === data.country_of_origin);
              setClientSuggestedCountry({
                code: data.country_of_origin,
                label: countryObject ? countryObject.label : data.country_of_origin
              });
            } else {
              setClientSuggestedCountry(null);
            }
          }
        } catch (error) {
          console.error('Error fetching client ASIN data:', error);
          setClientSuggestedCountry(null);
        }
      }
    };

    fetchClientAsinData();
  }, [item.item.asin, clientInfo.country, clientInfo.id, teamId]);

  const getHtsSuggestions = async () => {
    setIsLoadingHts(true);
    setHtsSuggestions(null);
    
    try {
      const response = await fetch(`${SERVER_API_URL}/api/suggest-hts-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: item.item.title,
          asin: item.item.asin,
          countryOfOrigin: importInfo.countryOfOrigin
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || 'Failed to get HTS suggestions');
      }

      const data = await response.json();
      console.log('Received HTS suggestions:', data);

      // Use the HTS_ID from the first match as the HTS code
      if (data.matches && data.matches.length > 0) {
        const suggestedHtsCode = data.matches[0].HTS_id.toString();
        handleHtsChange(suggestedHtsCode); // This will update state and save to database
      }

      setHtsSuggestions(data);

    } catch (error) {
      console.error('Error getting HTS suggestions:', error);
      setHtsSuggestions(null);
    } finally {
      setIsLoadingHts(false);
    }
  };

  // Update the useEffect for checking duties
  useEffect(() => {
    const checkDuties = async () => {
      if (importInfo.countryOfOrigin === 'CN' && importInfo.htsCode) {
        try {
          // Update HTS suggestions with duties info
          setHtsSuggestions(prev => prev ? {
            ...prev,
            duties: {
              feb2025Tariff: "Additional tariffs may apply in Feb 2025"
            }
          } : null);
          
        } catch (error) {
          console.error('Error checking duties:', error);
        }
      }
    };

    checkDuties();
  }, [importInfo.countryOfOrigin, importInfo.htsCode]);

  const DutiesDisplay = ({ duties }) => {
    if (!duties) return null;

    return (
      <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="text-lg font-semibold text-red-600 mb-2">Duties Information:</h4>
        {duties.section301 ? (
          <div className="mb-2">
            <span className="font-medium">Section 301 Duty: </span>
            <span className="text-red-600">{duties.section301}</span>
          </div>
        ) : (
          <div className="mb-2">
            <span className="font-medium">Section 301 Duty: </span>
            <span className="text-gray-600">No Section 301 duties found</span>
          </div>
        )}
        {duties.feb2025Tariff && (
          <div>
            <span className="font-medium">Additional Tariff: </span>
            <span className="text-red-600">{duties.feb2025Tariff}</span>
          </div>
        )}
      </div>
    );
  };

  // Add this effect to watch for country changes
  useEffect(() => {
    setIsHtsEnabled(importInfo.countryOfOrigin === 'CN');
  }, [importInfo.countryOfOrigin]);

  const checkForImageUpdates = async () => {
    try {
      console.log('Checking for updates...', {
        asin: item.item.asin,
        lastUpdate: lastImageUpdate,
        lastKnownPhotoCount
      });

      const response = await fetch(
        `${SERVER_API_URL}/api/check-image-updates/${item.item.asin}?lastUpdate=${lastImageUpdate || ''}&lastKnownCount=${lastKnownPhotoCount}`
      );
      
      if (!response.ok) throw new Error('Failed to check for updates');
      
      const data = await response.json();
      console.log('Update check response:', data);
      
      if (data.hasUpdates || data.currentPhotoCount > lastKnownPhotoCount) {
        const importResponse = await fetch(`${SERVER_API_URL}/api/usa-import-info/${item.item.asin}`);
        if (!importResponse.ok) throw new Error('Failed to fetch updated import info');
        
        const importData = await importResponse.json();
        console.log('New import data:', importData);
        
        if (importData) {
          // Ensure the data structure is complete before updating state
          setImportInfo({
            countryOfOrigin: importData.country_of_origin || '',
            htsCode: importData.hts_code || '',
            photos: importData.origin_photos || [],
            last_updated: importData.last_updated || null,
            created_at: importData.created_at || null,
            updated_by: importData.updated_by || null
          });
          setLastImageUpdate(data.lastUpdate);
          setLastKnownPhotoCount(data.currentPhotoCount);
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Error checking for image updates:', error);
      return false;
    }
  };

  const startPolling = () => {
    console.log('Starting polling with state:', {
      lastKnownPhotoCount,
      lastImageUpdate
    });
    
    // Clear any existing polling
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
    }
    
    setIsPolling(true);
    isPollingRef.current = true;
    pollStartTimeRef.current = Date.now();
    
    const poll = async () => {
      // First check timeout
      if (Date.now() - pollStartTimeRef.current > 180000) {
        console.log('Polling timeout reached');
        setIsPolling(false);
        isPollingRef.current = false;
        return;
      }

      try {
        const hasUpdates = await checkForImageUpdates();
        console.log('Poll result:', hasUpdates, 'isPolling:', isPollingRef.current);
        
        if (hasUpdates) {
          console.log('Updates found, stopping polling');
          setIsPolling(false);
          isPollingRef.current = false;
        } else if (isPollingRef.current) { // Check ref instead of state
          console.log('No updates yet, scheduling next poll...');
          pollingTimeoutRef.current = setTimeout(poll, 1000);
        } else {
          console.log('Polling was stopped, not scheduling next poll');
        }
      } catch (error) {
        console.error('Error during polling:', error);
        setIsPolling(false);
        isPollingRef.current = false;
      }
    };

    // Start the first poll
    poll();
  };

  const sendToDiscord = async () => {
    if (isProcessing) {
      console.log('Already processing a request');
      return;
    }

    setIsProcessing(true);
    
    try {
      // Stop any existing polling
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
      setIsPolling(false);
      isPollingRef.current = false;

      const imageUrl = (item.item.images && item.item.images.length > 0) 
        ? item.item.images[0].large_url 
        : 'https://qph.cf2.quoracdn.net/main-qimg-654617264f9192ec976abe6e53356240-lq';

      const response = await fetch(`${SERVER_API_URL}/api/discord-webhook`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          asin: item.item.asin,
          secret: 'Yyzprep123',
          imageUrl: imageUrl,
          title: item.item.title
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send to Discord');
      }

      // Reset states before starting new poll
      setLastKnownPhotoCount(0);
      setLastImageUpdate(null);
      
      // Small delay to ensure Discord bot has time to process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Start polling with fresh state
      startPolling();
    } catch (error) {
      console.error('Error sending to Discord:', error);
      alert('Failed to send to Discord');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle sequence printing with printer selection
  const handleSequencePrint = (quantity, sequenceSize) => {
    // Check if auto print is enabled
    const autoPrintEnabled = localStorage.getItem('autoPrintEnabled') === 'true';
    
    // Check for US client and missing country of origin
    if (clientInfo && clientInfo.country === 'USA' && !importInfo.countryOfOrigin) {
      showToast('Please select the Country of Origin for this US shipment before printing labels.', 'warning');
      return;
    }

    // Check for forbidden expiration date (Jan 1, 2030 placeholder)
    if (isForbiddenExpiryDate(expiryDate)) {
      showToast('Cannot print labels with temporary expiration date (Jan 1, 2030). Please set a proper expiration date or use a preset (1y, 2y, 3y).', 'warning');
      return;
    }

    if (autoPrintEnabled) {
      // Check if we have printers available
      if (availablePrinters.length === 0) {
        showToast('No printers available. Please check your printer settings.', 'warning');
        return;
      }
      
      // Show printer selection dialog for sequence printing
      setPendingPrintJob({ quantity, option: 'sequence', sequenceSize });
      setShowPrinterDialog(true);
    } else {
      // Proceed with normal sequence printing (no auto print)
      executeSequencePrint(quantity, sequenceSize, null);
    }
    
    setShowSequenceDialog(false);
  };

  const handleSyncFnsku = async () => {
    setIsSyncingFnsku(true);
    setFnskuSyncMessage('');
    
    try {
      // Get the client ID, with fallbacks
      const clientId = teamId ? teamId.toString() : 
                     (clientInfo && clientInfo.id ? clientInfo.id.toString() : '204');
      
      const response = await fetch('https://app.yyzprep.ca/api/channels/sync-fnskus?api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'x-selected-client-id': clientId
        },
        body: JSON.stringify({ 
          skus: [item.item.merchant_sku] 
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.message === "Completed checking for FNSKUs") {
        setFnskuSyncMessage('FNSKU successfully synced!');
        // Show toast notification
        showToast('FNSKU successfully synced!', 'success');
        
        // Reload data after sync
        reloadAsinData();
      } else {
        setFnskuSyncMessage('Failed to sync FNSKU');
        showToast('Failed to sync FNSKU', 'error');
      }
    } catch (error) {
      console.error('Error syncing FNSKU:', error);
      setFnskuSyncMessage('Error syncing FNSKU');
      showToast('Error syncing FNSKU', 'error');
    } finally {
      setIsSyncingFnsku(false);
    }
  };

  // History functionality - adapted from BoxSidebar
  const fetchBoxHistory = async (asin) => {
    try {
      setHistoryData(prev => ({ ...prev, loading: true }));
      const response = await fetch(`${API_URL}/api/box-history/${asin}?shipment_id=${shipmentId}`);
      if (!response.ok) throw new Error('Failed to fetch history');
      const data = await response.json();
      setHistoryData(prev => ({ 
        ...prev, 
        history: data, 
        loading: false 
      }));
    } catch (error) {
      console.error('Error fetching box history:', error);
      setHistoryData(prev => ({ ...prev, loading: false, history: [] }));
      showToast('Failed to fetch box history', 'error');
    }
  };

  const handleViewHistory = () => {
    setHistoryData({
      asin: item.item.asin,
      title: item.item.title,
      history: [],
      loading: false
    });
    setShowHistoryDialog(true);
    fetchBoxHistory(item.item.asin);
  };

  const getProductInfo = (item, asinInfo) => {
    const title = item.title || asinInfo.title || 'Product title not available';
    const image = item.image_url || item.main_image_url || asinInfo.image_url || 'https://qph.cf2.quoracdn.net/main-qimg-654617264f9192ec976abe6e53356240-lq';
    return { title, image };
  };

  const handleCreateBoxFromConfig = async (record) => {
    try {
      // Check if shipmentId is available
      if (!shipmentId) {
        throw new Error('Shipment ID is not available');
      }

      // Step 1: Create a new empty box with the historical dimensions/weight
      const newBoxNumber = (boxes?.length || 0) + 1;
      const newBox = {
        boxNumber: newBoxNumber,
        items: [],
        LENGTH: parseFloat(record.LENGTH),
        WIDTH: parseFloat(record.WIDTH),
        HEIGHT: parseFloat(record.HEIGHT),
        WEIGHT: parseFloat(record.WEIGHT),
        OUTBOUND_ID: shipmentId,
        employee: selectedNickname
      };

      const createBoxResponse = await fetch(`${API_URL}/api/add_box_outbound`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newBox)
      });

      const createBoxResult = await createBoxResponse.json();
      if (!createBoxResult.success) {
        throw new Error(`Failed to create box: ${createBoxResult.error || 'Unknown error'}`);
      }

      // Step 2: Add items from the historical configuration to the new box
      if (record.ASIN_LIST) {
        const historicalItems = record.ASIN_LIST.split(',').map(item => {
          const [asin, qty] = item.split(':');
          const shipmentItem = shipmentItems.find(si => si.item?.asin === asin.trim());
          if (shipmentItem) {
            // Get the item weight from asinData
            const asinInfo = asinData?.find(data => data.ASIN === asin.trim()) || {};
            let itemWeight = asinInfo.WEIGHT || '';
            
            // Fallback to shipment item weight if available
            if (!itemWeight && shipmentItem.item?.weight) {
              itemWeight = shipmentItem.item.weight;
            }

            return {
              shipmentId: shipmentId,
              boxNumber: newBoxNumber,
              sku: shipmentItem.item.merchant_sku,
              asin: shipmentItem.item.asin,
              fnsku: shipmentItem.item.fnsku,
              qty: parseInt(qty.trim(), 10),
              // Include historical box dimensions and weight to preserve them
              length: parseFloat(record.LENGTH),
              width: parseFloat(record.WIDTH), 
              height: parseFloat(record.HEIGHT),
              weight: parseFloat(record.WEIGHT), // Use historical TOTAL box weight, not individual item weight
              title: shipmentItem.item.title,
              pallet: shipmentItem.item.pallet,
              employee: selectedNickname,
              images: shipmentItem.item.images
            };
          }
          return null;
        }).filter(Boolean);

        console.log('Items to add:', historicalItems);

        // Add each item to the box
        for (const item of historicalItems) {
          try {
            console.log(`Adding item ${item.sku} to box ${newBoxNumber}:`, item);
            
            const addItemResponse = await fetch(`${API_URL}/api/add_item_to_box_outbound`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(item)
            });

            const addItemResult = await addItemResponse.json();
            
            if (!addItemResult.success) {
              console.error(`Failed to add item ${item.sku} to box ${newBoxNumber}:`, addItemResult.error);
            } else {
              console.log(`Successfully added item ${item.sku} to box ${newBoxNumber}`);
            }
          } catch (error) {
            console.error(`Error adding item ${item.sku} to box:`, error);
          }
        }
      }

      // Refresh boxes and show success message
      await refreshBoxes();
      
      setHistoryNotification({
        show: true,
        message: `New box created from configuration!`
      });
      showToast(`New box created from previous configuration`, 'success');
      
      setTimeout(() => {
        setHistoryNotification({ show: false, message: '' });
        setShowHistoryDialog(false);
      }, 2000);
    } catch (error) {
      console.error('Error creating box from configuration:', error);
      showToast('Failed to create box from configuration', 'error');
    }
  };

  // Now do the validity check
  if (!item || !item.item) {
    console.warn('Invalid item data received:', item);
    return null;
  }

  // Modify the photo click handler
  const handlePhotoClick = (photo, index) => {
    setSelectedPhotoPreview({ show: true, photo, index });
  };

  // Add handlePhotoPreviewDelete
  const handlePhotoPreviewDelete = () => {
    setDeletePhotoConfirm({ show: true, index: selectedPhotoPreview.index });
    setSelectedPhotoPreview({ show: false, photo: null, index: null });
  };

  // Function to execute the actual print job
  const executePrePrint = (quantity, option, selectedPrinter) => {
    generatePDF(
      item, 
      quantity, 
      expiryDate, 
      option || printOption, 
      useSeparator ? separatorInterval : 0,
      0,
      selectedPrinter,
      (result) => {
        if (result.success) {
          // Only show notification for auto print
          if (selectedPrinter) {
            showToast('Print job submitted successfully', 'success');
          }
        } else {
          // Always show error notifications
          showToast(result.message, 'error');
        }
      },
      clientInfo,
      importInfo.countryOfOriginLabel
    );
    setPrintOption('');
  };

  // Handle printer selection from dialog
  const handlePrinterSelect = (printer) => {
    setShowPrinterDialog(false);
    
    if (pendingPrintJob && printer) {
      // Save selected printer ID for future use (matching Shipment component format)
      localStorage.setItem('selectedPrinter', printer.id);
      
      // Check if this is a sequence print job
      if (pendingPrintJob.option === 'sequence' && pendingPrintJob.sequenceSize) {
        // Execute sequence print job with selected printer ID
        executeSequencePrint(pendingPrintJob.quantity, pendingPrintJob.sequenceSize, printer.id);
      } else if (pendingPrintJob.option === null) {
        // Execute quick print job with selected printer ID
        executeQuickPrint(pendingPrintJob.quantity, printer.id);
      } else {
        // Execute regular print job with selected printer ID
        executePrePrint(pendingPrintJob.quantity, pendingPrintJob.option, printer.id);
      }
    }
    
    setPendingPrintJob(null);
  };

  // Function to execute sequence print
  const executeSequencePrint = (quantity, sequenceSize, selectedPrinter) => {
    generatePDF(
      item,
      quantity,
      expiryDate,
      'sequence',
      useSeparator ? separatorInterval : 0,
      sequenceSize,
      selectedPrinter,
      (result) => {
        if (result.success) {
          if (selectedPrinter) {
            showToast('Print job submitted successfully', 'success');
          }
        } else {
          showToast(result.message, 'error');
        }
      },
      clientInfo,
      importInfo.countryOfOriginLabel
    );
    setPrintOption('');
  };

  // Add debug useEffects


  const handlePrintClick = (event) => {
    // Calculate position for the dropdown, ensuring it's not cut off
    const dropdownDimensions = { width: 320, height: 480 }; // Approximate size
    const position = calculateDropdownPosition(event.currentTarget, dropdownDimensions);
    
    setPrintDropdownPosition(position);
    setSelectedPrintItem(item);
    setCustomPrintQty(totalQuantity.toString()); // Prefill with shipment quantity
    setShowPrintDropdown(true);
  };

  const resetPrintDropdown = () => {
    setShowPrintDropdown(false);
    setSelectedPrintItem(null);
    setPrintQuantity(1);
    setCustomPrintQty('');
    setUseSeparator(false);
    setSeparatorInterval(10);
    setExtraLabelType('none');
    setSequenceMode(false);
    setSequenceCount(3);
    setPrintStep('config'); // Reset to config step
  };

  const handlePrintNext = () => {
    if (!selectedPrintItem) return;

    const quantity = printQuantity === 'custom' ? parseInt(customPrintQty) || 1 : printQuantity;
    
    // Validate inputs
    if (quantity < 1) {
      showToast('Please enter a valid quantity', 'destructive');
      return;
    }
    
    if (useSeparator && separatorInterval < 1) {
      showToast('Please enter a valid separator interval', 'destructive');
      return;
    }

    if (sequenceMode && sequenceCount < 1) {
      showToast('Please enter a valid sequence count (minimum 1)', 'destructive');
      return;
    }

    // Check if auto print is enabled
    const autoPrintEnabled = localStorage.getItem('autoPrintEnabled') === 'true';
    
    if (autoPrintEnabled) {
      // Check if we have printers available
      if (availablePrinters.length === 0) {
        showToast('No printers available. Please check your printer settings.', 'warning');
        return;
      }
      
      // Go to printer selection step
      setPrintStep('printer');
    } else {
      // If auto print is disabled, proceed directly to print
      handlePrintExecute();
    }
  };

  const handlePrintExecute = (printerIdOverride = null) => {
    if (!selectedPrintItem) return;

    // Check for US client and missing country of origin
    if (clientInfo && clientInfo.country === 'USA' && !importInfo.countryOfOrigin) {
      showToast('Please select the Country of Origin for this US shipment before printing labels.', 'warning');
      return;
    }

    // Check for forbidden expiration date (Jan 1, 2030 placeholder)
    if (isForbiddenExpiryDate(expiryDate)) {
      showToast('Cannot print labels with temporary expiration date (Jan 1, 2030). Please set a proper expiration date or use a preset (1y, 2y, 3y).', 'warning');
      return;
    }

    const quantity = printQuantity === 'custom' ? parseInt(customPrintQty) || 1 : printQuantity;
    const autoPrintEnabled = localStorage.getItem('autoPrintEnabled') === 'true';
    const effectivePrinterId = printerIdOverride || selectedPrinter;
    
    // If auto print is enabled, validate printer selection
    if (autoPrintEnabled && !effectivePrinterId) {
      showToast('Please select a printer before printing.', 'warning');
      return;
    }
    
    // Save the selected printer if auto print is enabled
    if (autoPrintEnabled && effectivePrinterId) {
      localStorage.setItem('selectedPrinter', effectivePrinterId);
      setSelectedPrinter(effectivePrinterId); // Update state for UI consistency
    }
    
    // Determine the label type for the generator
    let labelType = null;
    if (sequenceMode) {
      labelType = 'sequence';
    } else if (extraLabelType === 'Ready to Ship') {
      labelType = 'Ready to Ship';
    } else if (extraLabelType === 'Sold as a Set') {
      labelType = 'Sold as Set';
    } else if (extraLabelType === 'Suffocation Warning') {
      labelType = 'Suffocation Warning';
    }
    
    // Call the label generator
    try {
      generatePDF(
        selectedPrintItem,
        quantity,
        expiryDate,
        labelType,
        useSeparator ? separatorInterval : 0,
        sequenceMode ? sequenceCount : 0,
        autoPrintEnabled ? effectivePrinterId : null,
        (result) => {
          if (result.success) {
            showToast(result.message, 'success');
          } else {
            showToast(result.message, 'error');
          }
        },
        clientInfo,
        importInfo.countryOfOriginLabel
      );
    } catch (error) {
      console.error('Error generating labels:', error);
      showToast(`Error generating labels: ${error.message}`, 'error');
    }
    
    resetPrintDropdown();
  };

  const handlePrintBack = () => {
    setPrintStep('config');
  };

  // Quick print function for one-click printing
  const handleQuickPrint = (quantity) => {
    // Check for US client and missing country of origin
    if (clientInfo && clientInfo.country === 'USA' && !importInfo.countryOfOrigin) {
      showToast('Please select the Country of Origin for this US shipment before printing labels.', 'warning');
      return;
    }
    
    // Check for forbidden expiration date (Jan 1, 2030 placeholder)
    if (isForbiddenExpiryDate(expiryDate)) {
      showToast('Cannot print labels with temporary expiration date (Jan 1, 2030). Please set a proper expiration date or use a preset (1y, 2y, 3y).', 'warning');
      return;
    }
    
    // Check if auto print is enabled
    if (autoPrintEnabled) {
      // Check if we have printers available
      if (availablePrinters.length === 0) {
        showToast('No printers available. Please check your printer settings.', 'warning');
        return;
      }
      
      // Show printer selection dialog for quick print
      setPendingPrintJob({ quantity, option: null });
      setShowPrinterDialog(true);
    } else {
      // Proceed with normal printing (no auto print)
      executeQuickPrint(quantity, null);
    }
  };

  // Function to execute quick print
  const executeQuickPrint = (quantity, selectedPrinter) => {
    // Call the label generator
    try {
      generatePDF(
        item,
        quantity,
        expiryDate,
        null, // No special label type
        0, // No separator
        0, // No sequence
        selectedPrinter, // Use selected printer if auto print enabled, null otherwise
        (result) => {
          // Handle the callback from generatePDF
          if (result.success) {
            showToast(result.message, 'success');
          } else {
            showToast(result.message, 'error');
          }
        },
        clientInfo,
        importInfo.countryOfOriginLabel
      );
    } catch (error) {
      console.error('Error generating labels:', error);
      showToast(`Error generating labels: ${error.message}`, 'error');
    }
  };



  // Add this function to validate quantity consistency (SKU-only)
  const validateQuantityConsistency = useCallback(() => {
    if (!boxes || !Array.isArray(boxes)) return 0;
    
    let calculatedQuantity = 0;
    
    boxes.forEach(box => {
      if (box.items && Array.isArray(box.items)) {
        box.items.forEach(boxItem => {
          // Match ONLY by SKU - no ASIN fallback
          if (boxItem.SKU === item.item.merchant_sku) {
            const qty = Number(boxItem.QTY || 0);
            calculatedQuantity += qty;
          }
        });
      }
    });
    
    // Only log if there's a mismatch
    if (calculatedQuantity !== quantityInBoxes) {
      console.warn(`[OutboundItem] SKU-only quantity mismatch for ${item.item.merchant_sku}: calculated=${calculatedQuantity}, passed=${quantityInBoxes}`);
    }
    
    return calculatedQuantity;
  }, [boxes, item.item.merchant_sku, quantityInBoxes]);

  // Use validated quantity for display
  const displayQuantityInBoxes = validateQuantityConsistency();

  // Add this useEffect to track quantity prop changes
  useEffect(() => {
    // Only log for significant changes or mismatches
    if (Math.abs(quantityInBoxes - item.quantity) > 0) {
      console.log(`[OutboundItem] Quantity change for ${item.item.merchant_sku}: quantityInBoxes=${quantityInBoxes}, totalQuantity=${item.quantity}`);
    }
  }, [quantityInBoxes, item.quantity, item.item.merchant_sku]);

  // Check if item is fully boxed (no more units remaining)
  const isFullyBoxed = displayQuantityInBoxes === totalQuantity;

  return (
    <div className={`text-sm border-b border-gray-200 relative ${
      isFullyBoxed 
        ? 'bg-green-100' 
        : ''
    }`} key={item.id || item.item.merchant_sku}>
      {/* Table Row Layout */}
      <div className="flex" style={{ maxWidth: '950px' }}>
        {/* Image Cell */}
        <div className="px-2 py-3 align-top whitespace-nowrap flex-shrink-0" style={{ width: '120px' }}>
          {/* Bulk Selection Checkbox */}
          <div className="mb-2 flex justify-center">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => onSelect(e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              title="Select for bulk printing"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          
          <div className="relative h-20 w-20 flex-shrink-0">
            <img
              src={currentImageUrl}
              alt={item.item.title || 'Product image'}
              className="h-20 w-20 rounded object-cover cursor-pointer"
              onClick={() => setShowImagePreview(true)}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://via.placeholder.com/80?text=No+Img';
              }}
            />
            <button
              onClick={sendToDiscord}
              disabled={isProcessing}
              className="absolute top-0 right-0 p-0.5 bg-gray-800 bg-opacity-50 text-white rounded-bl-md hover:bg-opacity-75 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              title={isProcessing ? "Processing..." : "Send to Discord"}
            >
              <FaDiscord className="h-4 w-4" />
            </button>
          </div>
          
          {/* Quantity Display under image */}
          <div className="flex items-center justify-center mt-2 text-sm font-medium text-gray-700">
            <span 
              title={`Quantity: ${displayQuantityInBoxes}/${totalQuantity} | SKU: ${item.item.merchant_sku}`}
              onClick={() => console.log(`[OutboundItem] Click debug - SKU: ${item.item.merchant_sku}, quantityInBoxes: ${displayQuantityInBoxes}, totalQuantity: ${totalQuantity}`)}
              className="cursor-pointer"
            >
              {displayQuantityInBoxes}
            </span>
            <span className="mx-1">/</span>
            <span>{totalQuantity}</span>
          </div>
        </div>

        {/* Product Details Cell */}
        <div className="px-2 py-3 align-top flex-shrink-0" style={{ width: '240px' }}>
          {/* Product Name */}
          <div className="font-medium text-gray-900 line-clamp-2" title={item.item.title}>
            {highlightTextWithTruncation(
              item.item.title,
              item._searchTerm,
              item._searchMatches?.fields?.includes('title'),
              60
            )}
          </div>

          {/* Identifiers Block */}
          <div className="mt-1 space-y-0.5">
            {/* ASIN */}
            <div className="text-sm text-gray-700 font-medium flex items-center">
              <button 
                onClick={() => copyToClipboard(item.item.asin, 'ASIN')} 
                className="mr-2 text-gray-400 hover:text-blue-600 flex-shrink-0"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
              </button>
              <span className="whitespace-nowrap">ASIN:</span>
              <a
                href={`https://${clientInfo.name.includes('(CA)') ? 'amazon.ca' : 'amazon.com'}/dp/${item.item.asin}`}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-1 truncate text-blue-600 hover:text-blue-800"
                title={item.item.asin}
              >
                {highlightText(item.item.asin, item._searchTerm, item._searchMatches?.fields?.includes('asin')) || 'N/A'}
              </a>
            </div>

            {/* SKU */}
            <div className="text-sm text-gray-700 font-medium flex items-center">
              <button 
                onClick={() => copyToClipboard(item.item.merchant_sku, 'SKU')} 
                className="mr-2 text-gray-400 hover:text-blue-600 flex-shrink-0"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
              </button>
              <span className="whitespace-nowrap">SKU:</span>
              <a
                href={`https://app.yyzprep.ca/staff/inventory/${item.item.id}#history`}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-1 truncate text-blue-600 hover:text-blue-800"
                title={item.item.merchant_sku}
              >
                {highlightText(item.item.merchant_sku, item._searchTerm, item._searchMatches?.fields?.includes('sku')) || 'N/A'}
              </a>
            </div>

            {/* FNSKU */}
            <div className="text-sm text-gray-700 font-medium flex items-center">
              <button 
                onClick={() => copyToClipboard(item.item.fnsku, 'FNSKU')} 
                className="mr-2 text-gray-400 hover:text-blue-600 flex-shrink-0"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
              </button>
              <span className="whitespace-nowrap">FNSKU:</span>
              <span className="ml-1 truncate" title={item.item.fnsku}>
                {highlightText(item.item.fnsku, item._searchTerm, item._searchMatches?.fields?.includes('fnsku')) || 'N/A'}
              </span>
            </div>

            {/* UPC/EAN (Conditional) */}
            {showMoreIdentifiers && (() => {
              const { upcs, eans } = getUPCsAndEANs();
              return (
                <>
                  {upcs.length > 0 && upcs.map((upc, index) => {
                    const isHighlighted = item?._searchMatches?.upcs?.includes(upc);
                    return (
                    <div key={`upc-${index}`} className="text-xs text-gray-500 flex items-center">
                      <button 
                        onClick={() => copyToClipboard(upc, `UPC ${index + 1}`)} 
                        className="mr-2 text-gray-400 hover:text-blue-600 flex-shrink-0"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                        </svg>
                      </button>
                      <span className="whitespace-nowrap">UPC:</span>
                        <span className="ml-1 truncate" title={upc}>
                          {isHighlighted ? highlightText(upc, item._searchTerm, true) : upc}
                        </span>
                    </div>
                    );
                  })}
                  {eans.length > 0 && eans.map((ean, index) => {
                    const isHighlighted = item?._searchMatches?.eans?.includes(ean);
                    return (
                    <div key={`ean-${index}`} className="text-xs text-gray-500 flex items-center">
                      <button 
                        onClick={() => copyToClipboard(ean, `EAN ${index + 1}`)} 
                        className="mr-2 text-gray-400 hover:text-blue-600 flex-shrink-0"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                        </svg>
                      </button>
                      <span className="whitespace-nowrap">EAN:</span>
                        <span className="ml-1 truncate" title={ean}>
                          {isHighlighted ? highlightText(ean, item._searchTerm, true) : ean}
                        </span>
                    </div>
                    );
                  })}
                </>
              );
            })()}

            {/* More/Less Identifiers Button */}
            {(() => {
              const { upcs, eans } = getUPCsAndEANs();
              if (upcs.length > 0 || eans.length > 0) {
                return (
                  <button
                    onClick={() => setShowMoreIdentifiers(!showMoreIdentifiers)}
                    className="text-xs text-blue-600 hover:underline mt-1"
                  >
                    {showMoreIdentifiers ? 'Less Identifiers' : 'More Identifiers'}
                  </button>
                );
              }
              return null;
            })()}

            {/* View Previous Configurations Button */}
            <button
              onClick={handleViewHistory}
              className="text-xs text-blue-600 hover:underline mt-1 block"
            >
              View Previous Configurations
            </button>

            {/* Dimensions */}
            <div className="text-xs text-gray-500 mt-2">
              <span className="whitespace-nowrap">Dims:</span>
              <span className="ml-1">
                {formatDimensions(item.item.length_mm, item.item.width_mm, item.item.height_mm)}
              </span>
            </div>

            {/* Oversized Warning */}
            {oversizeTag && (
              <div className="mt-1">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  oversizeTag === 'Huge Oversized' 
                    ? 'bg-red-100 text-red-800 border border-red-200' 
                    : 'bg-orange-100 text-orange-800 border border-orange-200'
                }`}>
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.232 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  {oversizeTag} - Fee Required
                </span>
              </div>
            )}
          </div>
        </div>



        {/* Prep & Services Cell */}
        <div className="px-2 py-3 align-top relative flex-shrink-0" style={{ width: '150px' }}>
          {/* Prep Requirements as Chips */}
          <div className="mb-2">
            <div className="text-xs font-medium text-gray-700 mb-1">Prep:</div>
            <div className="flex flex-wrap gap-1">
              {isLoadingPrep ? (
                <span className="text-xs text-gray-500">Loading...</span>
              ) : prepRequirements[item.item.merchant_sku]?.length > 0 ? (
                prepRequirements[item.item.merchant_sku].map((prep, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200"
                  >
                    {prep}
                  </span>
                ))
              ) : (
                <span className="text-xs text-gray-500">N/A</span>
              )}
            </div>
          </div>

          {/* Services as Chips */}
          <div className="mb-2">
            <div className="text-xs font-medium text-blue-600 mb-1">Services:</div>
            <div className="flex flex-wrap gap-1">
              {selectedServices && selectedServices.length > 0 ? (
                selectedServices.map((service, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200"
                  >
                    {service.label}
                  </span>
                ))
              ) : (
                <span className="text-xs text-gray-500">None</span>
              )}
            </div>
          </div>

          {/* Add Service Button & Dropdown */}
          <div className="relative mt-1">
            <button
              onClick={() => setShowServicesDropdown(!showServicesDropdown)}
              disabled={isUpdatingServices}
              className={`text-xs mt-1 focus:outline-none flex items-center gap-1 ${
                isUpdatingServices 
                  ? 'text-gray-400 cursor-not-allowed' 
                  : 'text-blue-600 hover:underline'
              }`}
            >
              {isUpdatingServices && (
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
              )}
              {isUpdatingServices ? 'Updating...' : 'Add Service'}
            </button>

            {showServicesDropdown && (
              <div 
                ref={servicesDropdownRef}
                className="origin-top-left absolute left-0 top-6 w-80 z-[9999] rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
              >
                <div className="p-3 border-b border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                      Manage Services
                      {isUpdatingServices && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      )}
                    </div>
                    <button 
                      onClick={() => setShowServicesDropdown(false)}
                      className="p-1 hover:bg-gray-200 rounded text-gray-500 hover:text-gray-800"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <input
                    type="text"
                    placeholder="Search services..."
                    disabled={isUpdatingServices}
                    className={`w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:ring-blue-500 focus:border-blue-500 ${
                      isUpdatingServices 
                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed' 
                        : 'bg-white text-gray-900'
                    }`}
                    value={serviceSearchTerm}
                    onChange={(e) => setServiceSearchTerm(e.target.value)}
                  />
                  
                  {/* Currently Selected Services Chips */}
                  {selectedServices && selectedServices.length > 0 && (
                    <div className="mt-2">
                      <div className="text-xs font-medium text-gray-600 mb-1.5">Selected Services:</div>
                      <div className="flex flex-wrap gap-1">
                        {selectedServices.map((service, index) => (
                          <div 
                            key={index}
                            className="inline-flex items-center bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-md"
                          >
                            <span className="truncate max-w-32">{service.label}</span>
                            <button
                              onClick={async () => {
                                if (isUpdatingServices) return;
                                
                                const newSelectedServices = selectedServices.filter((_, i) => i !== index);
                                setSelectedServices(newSelectedServices);
                                
                                // Auto-save changes
                                await handleServiceChange(newSelectedServices);
                              }}
                              disabled={isUpdatingServices}
                              className={`ml-1 rounded-full p-0.5 transition-colors ${
                                isUpdatingServices 
                                  ? 'cursor-not-allowed text-gray-400' 
                                  : 'hover:bg-blue-200'
                              }`}
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="py-1 max-h-60 overflow-y-auto">
                  {filteredServiceOptions.length > 0 ? (
                    filteredServiceOptions.map((service) => (
                      <label key={service.value} className={`flex items-center px-3 py-1.5 text-xs ${
                        isUpdatingServices 
                          ? 'cursor-not-allowed text-gray-400 bg-gray-50' 
                          : 'cursor-pointer text-gray-700 hover:bg-gray-100'
                      }`}>
                        <input
                          type="checkbox"
                          disabled={isUpdatingServices}
                          className={`form-checkbox h-3.5 w-3.5 rounded border-gray-300 focus:ring-blue-500 ${
                            isUpdatingServices 
                              ? 'text-gray-400 cursor-not-allowed' 
                              : 'text-blue-600'
                          }`}
                          checked={selectedServices?.some(s => s.value === service.value) || false}
                          onChange={async (e) => {
                            if (isUpdatingServices) return;
                            
                            let newSelectedServices;
                            if (e.target.checked) {
                              newSelectedServices = [...(selectedServices || []), service];
                            } else {
                              newSelectedServices = selectedServices?.filter(s => s.value !== service.value) || [];
                            }
                            setSelectedServices(newSelectedServices);
                            
                            // Auto-save changes
                            await handleServiceChange(newSelectedServices);
                          }}
                        />
                        <span className="ml-2">{service.label}</span>
                      </label>
                    ))
                  ) : (
                    <p className="px-3 py-1.5 text-xs text-gray-500">No services found</p>
                  )}
                </div>
              </div>
            )}
          </div>


        </div>

        {/* Exp/Weight/Shelving Cell */}
        <div className="px-2 py-3 align-top flex-shrink-0" style={{ width: '200px' }}>
          {/* Expiration Date */}
          {/* Warning for OpenMarketplace clients */}
          {clientInfo?.name?.toLowerCase().includes('openmarket') && expiryDate && (() => {
            const currentDate = new Date();
            const differenceInDays = (expiryDate - currentDate) / (1000 * 60 * 60 * 24);
            return differenceInDays < 362;
          })() && (
            <div className="mb-2 px-2 py-1 bg-red-100 border border-red-400 rounded text-xs text-red-800 font-bold">
              ⚠️ Client wants min. 365 days
            </div>
          )}
          <div className="flex items-center text-xs sm:text-sm">
            <strong className="w-10">Exp:</strong>
            <div className="flex-1 flex items-center gap-2">
              <ExpirationDatePicker
                value={expiryDate ? expiryDate.toISOString().split('T')[0] : ''}
                onChange={handleExpiryDateChange}
                onBlur={handleExpiryDateBlur}
                disabled={isUpdatingExpiry}
                className={`w-full max-w-[170px] ${
                  isUpdatingExpiry ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              />
              {isUpdatingExpiry && (
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                  <span>Updating...</span>
                </div>
              )}
            </div>
          </div>

          {/* Weight */}
          <div className="flex items-center mt-2 text-xs sm:text-sm">
            <strong className="w-10">Kgs:</strong>
            {editingWeight ? (
                <input
                  type="number"
                  value={weight}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow empty string or valid number input
                  if (value === '' || (!isNaN(value) && !isNaN(parseFloat(value)))) {
                    setWeight(value);
                  }
                }}
                onBlur={(e) => {
                  // Format to max 2 decimal places on blur
                  const formattedValue = e.target.value ? parseFloat(e.target.value).toFixed(2) : '';
                  setWeight(formattedValue);
                  handleWeightBlur(e);
                    setEditingWeight(false);
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                    // Format to max 2 decimal places on Enter
                    const formattedValue = e.target.value ? parseFloat(e.target.value).toFixed(2) : '';
                    setWeight(formattedValue);
                    handleWeightBlur(e);
                      setEditingWeight(false);
                    }
                  }}
                className="w-[60px] sm:w-20 p-1.5 border border-gray-300 rounded-md shadow-sm text-xs sm:text-sm bg-white text-gray-900 focus:ring-blue-500 focus:border-blue-500 relative z-[100]"
                  step="0.01"
                min="0"
                  autoFocus
                />
            ) : (
              <div 
                className={`cursor-pointer ${
                  weight && !isNaN(parseFloat(weight)) 
                    ? "text-gray-700 hover:text-blue-600" 
                    : "text-red-600 font-bold hover:text-red-700"
                }`}
                onClick={() => {
                  console.log('🔧 [OutboundItem] Weight display click:', { 
                    asin: item.item.asin, 
                    currentWeight: weight, 
                    asinDataWeight: asinDataItem.WEIGHT 
                  });
                  setEditingWeight(true);
                }}
              >
                {weight && !isNaN(parseFloat(weight)) ? `${parseFloat(weight).toFixed(2)}` : 'Not Set'}
              </div>
            )}
          </div>

          {/* Price - For US shipments only */}
          {clientInfo.country === 'USA' && (
            <div>
              <div className="flex items-center mt-2 text-xs sm:text-sm">
                <strong className="w-10">Price:</strong>
                <div className="flex items-center gap-1">
                  {editingPrice ? (
                    <input
                      type="number"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                                           onBlur={(e) => {
                         handlePriceBlur(e);
                         setEditingPrice(false);
                       }}
                       onKeyPress={(e) => {
                         if (e.key === 'Enter') {
                           handlePriceBlur(e);
                           setEditingPrice(false);
                         }
                       }}
                      className="w-[60px] sm:w-20 p-1.5 border border-gray-300 rounded-md shadow-sm text-xs sm:text-sm bg-white text-gray-900 focus:ring-blue-500 focus:border-blue-500 relative z-[100]"
                      step="0.01"
                      min="0"
                      autoFocus
                    />
                  ) : (
                    <div 
                      className="text-gray-700 cursor-pointer hover:text-blue-600"
                      onClick={() => setEditingPrice(true)}
                    >
                      {price && !isNaN(parseFloat(price)) ? `$${parseFloat(price).toFixed(2)}` : 'Not Set'}
                    </div>
                  )}
                  <button
                    onClick={() => handlePriceCheck(item.item.asin)}
                    disabled={isLoadingPrice}
                    className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    title="Update price from Keepa"
                  >
                    {isLoadingPrice ? (
                      <div className="w-3 h-3 border border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    )}
                  </button>
                  {priceNotification && (
                    <div className="text-green-500">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                    </div>
                  )}
                </div>
              </div>
              {price && parseFloat(price) > 0 && (
                <div className="pl-10 text-xs text-gray-500">
                  (fits {Math.floor(800 / parseFloat(price))} in box)
                </div>
              )}
            </div>
          )}

          {/* Shelving Section */}
          <div className="mt-3 border-t border-gray-200 pt-2">
            <div className="text-xs font-medium text-gray-700 mb-2">Shelving:</div>
            <div className="space-y-1 text-xs">
              {/* Existing Shelves - Only show if data exists */}
              {shelvingData && shelvingData.length > 0 && 
                shelvingData.filter(shelf => shelf.SKU === item.item.merchant_sku).length > 0 && (
                <>
                  {shelvingData
                    .filter(shelf => shelf.SKU === item.item.merchant_sku)
                    .map((shelf, index) => {
                      const key = shelf.KEY; // Use the existing KEY from the database
                      return (
                        <div key={key} className="flex items-center justify-between space-x-1">
                          <span className="text-gray-600 text-xs whitespace-nowrap">{shelf.FULL_SHELF_CODE}:</span>
                          <div className="flex items-center space-x-2">
                            {displayQuantityInBoxes > 0 && (
                                <button
                                    onClick={() => handleRemoveBoxedFromShelf(key, shelf.FULL_SHELF_CODE)}
                                    className="text-xs text-blue-600 hover:underline mr-2"
                                    title={`Remove ${displayQuantityInBoxes} boxed items from this shelf`}
                                >
                                    Remove {displayQuantityInBoxes}
                                </button>
                            )}
                          <input
                            type="number"
                            value={shelvingInputValues[key] !== undefined ? shelvingInputValues[key] : (shelf.QTY || shelf.Quantity || 0)}
                            onChange={(e) => handleShelvingQtyChange(key, e.target.value)}
                              onBlur={(e) => handleShelvingQtyBlur(key, shelf.FULL_SHELF_CODE, e)}
                            className="w-10 p-0.5 border border-gray-300 rounded text-xs bg-white text-gray-900 focus:ring-blue-500 focus:border-blue-500"
                            min="0"
                          />
                            {shelvingNotification && (
                              <div className="text-green-500 animate-pulse">
                                <svg className="h-2.5 w-2.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                            <button
                              onClick={() => handleRemoveShelf(key, shelf.FULL_SHELF_CODE)}
                              className="flex items-center justify-center w-3.5 h-3.5 text-red-500 hover:text-red-700 bg-white hover:bg-red-50 rounded border border-red-300"
                              title="Remove all items from this shelf"
                            >
                              <svg className="h-2 w-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                </>
              )}
              
              {/* Add Shelf Interface - Always visible */}
              {isAddingShelf ? (
                <div className="flex items-center space-x-1 mt-1">
                  <input
                    type="text"
                    placeholder="P-01-01"
                    value={newShelfCode}
                    onChange={(e) => setNewShelfCode(e.target.value.toUpperCase())}
                    className={`w-14 p-0.5 border rounded text-xs bg-white text-gray-900 focus:ring-blue-500 focus:border-blue-500 ${
                      newShelfCode && !validateShelfCode(newShelfCode.toUpperCase()) 
                        ? 'border-red-500' 
                        : 'border-gray-300'
                    }`}
                  />
                  <input
                    type="number"
                    placeholder="Qty"
                    value={newShelfQuantity}
                    onChange={(e) => setNewShelfQuantity(e.target.value)}
                    className="w-10 p-0.5 border border-gray-300 rounded text-xs bg-white text-gray-900 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                  />
                  <button
                    onClick={handleAddShelf}
                    className="flex items-center justify-center w-4 h-4 text-green-600 hover:text-green-800 hover:bg-green-50 rounded border border-green-300"
                    title="Add shelf"
                  >
                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      setIsAddingShelf(false);
                      setNewShelfCode('');
                      setNewShelfQuantity('');
                    }}
                    className="flex items-center justify-center w-4 h-4 text-red-500 hover:text-red-700 hover:bg-red-50 rounded border border-red-300"
                    title="Cancel"
                  >
                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="flex items-center">
                  <button
                    onClick={() => setIsAddingShelf(true)}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    + Add Shelf
                  </button>
                  {shelvingNotification && (
                    <div className="ml-2 text-green-500 animate-pulse">
                      <svg className="h-2.5 w-2.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Actions Cell */}
        <div className="px-1 py-3 text-xs font-medium align-top relative flex flex-col items-center space-y-1.5 flex-shrink-0" style={{ width: '80px' }}>
          {/* FDA and Prior Notice Classification Indicators - Only for US shipments */}
          {clientInfo.country === 'USA' && (
            <div className="flex flex-col items-center space-y-1">
              {/* FDA Classification */}
              <div className="flex items-center" title={
                isLoadingFda ? 'Checking FDA requirements...' :
                fdaClassification ? 
                  `FDA Classification: ${fdaClassification.reason}${fdaClassification.cached ? ' (cached)' : ''}` :
                  'FDA classification not available'
              }>
                {isLoadingFda ? (
                  <div className="w-4 h-4 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                ) : fdaClassification ? (
                  fdaClassification.fdaLikely ? (
                    <div className="flex items-center">
                      <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                      <span className="ml-1 text-xs text-red-600 font-semibold">FDA REQ</span>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="ml-1 text-xs text-green-600 font-semibold">NO FDA</span>
                    </div>
                  )
                ) : (
                  <div className="flex items-center">
                    <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                    <span className="ml-1 text-xs text-gray-500">FDA ?</span>
                  </div>
                )}
              </div>

              {/* Prior Notice Classification */}
              <div className="flex items-center" title={
                isLoadingFda ? 'Checking FDA Prior Notice requirements...' :
                fdaClassification ? 
                  `FDA Prior Notice: ${fdaClassification.priorNoticeRequired ? 'Required for food, beverages, dietary supplements, animal feed' : 'Not required for USA import'}${fdaClassification.cached ? ' (cached)' : ''}` :
                  'FDA Prior Notice classification not available'
              }>
                {isLoadingFda ? (
                  <div className="w-4 h-4 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                ) : fdaClassification ? (
                                     fdaClassification.priorNoticeRequired ? (
                     <div className="flex items-center">
                       <svg className="w-4 h-4 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                         <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                       </svg>
                       <span className="ml-1 text-xs text-orange-600 font-semibold">PN REQ</span>
                     </div>
                   ) : (
                     <div className="flex items-center">
                       <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                         <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                       </svg>
                       <span className="ml-1 text-xs text-green-600 font-semibold">No PN</span>
                     </div>
                   )
                ) : (
                  <div className="flex items-center">
                    <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                                         <span className="ml-1 text-xs text-gray-500">PN ?</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* US Customs Status Indicators */}
          {clientInfo.country === 'USA' && (
            <div className="flex flex-col items-center space-y-1">
              {/* Country of Origin Check */}
              <div
                className="flex items-center"
                title={
                  importInfo.countryOfOrigin
                    ? `Country: ${importInfo.countryOfOriginLabel || importInfo.countryOfOrigin}${clientSuggestedCountry ? ` | Client expects: ${clientSuggestedCountry.label}` : ''}`
                    : `Country of origin not set${clientSuggestedCountry ? ` | Client expects: ${clientSuggestedCountry.label}` : ''}`
                }
              >
                {importInfo.countryOfOrigin ? (
                  <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                )}
                <span className="ml-1 text-xs text-gray-600">Origin</span>
                {clientSuggestedCountry && (
                  <span className="ml-0.5 text-amber-500" title={`Client expects: ${clientSuggestedCountry.label}`}>*</span>
                )}
              </div>
              
              {/* Photos Check */}
              <div className="flex items-center" title={importInfo.photos && importInfo.photos.length > 0 ? `${importInfo.photos.length} photo(s) uploaded` : 'No photos uploaded'}>
                {importInfo.photos && importInfo.photos.length > 0 ? (
                  <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                )}
                <span className="ml-1 text-xs text-gray-600">Photo</span>
              </div>
            </div>
          )}

          {/* Customs Info Button */}
          {clientInfo.country === 'USA' && (
            <div className="relative">
              <button
                onClick={() => setShowCustomsModal(!showCustomsModal)}
                className="w-16 text-gray-600 hover:text-gray-800 font-semibold py-1 px-1 text-xs text-center rounded-md border border-gray-300 bg-white hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
              >
                Customs
              </button>

              {/* Customs Info Modal */}
              {showCustomsModal && (
                <div 
                  ref={customsModalRef}
                  className="absolute right-full mr-2 top-0 w-[350px] z-[99999] rounded-lg shadow-xl bg-white border border-gray-200 p-3 space-y-2.5 focus:outline-none"
                  style={{ 
                    maxHeight: '450px',
                    overflowY: 'auto'
                  }}
                >
                  {/* Header */}
                  <div className="flex justify-between items-center border-b border-gray-100 pb-2">
                    <h3 className="text-sm font-semibold text-gray-900">Customs Information</h3>
                    <button
                      onClick={() => setShowCustomsModal(false)}
                      className="text-gray-400 hover:text-gray-600 p-1 rounded-full focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* HTS Code */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">HTS Code</label>
                    <div className="flex items-center">
                      <input
                        type="text"
                        value={importInfo.htsCode || ''}
                        onChange={(e) => handleHtsChange(e.target.value)}
                        className="flex-grow px-2 py-1.5 border border-gray-300 rounded-l-md text-xs bg-white text-gray-900 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter HTS Code"
                      />
                      <button
                        onClick={getHtsSuggestions}
                        disabled={!isHtsEnabled || isLoadingHts}
                        className="px-2 py-1.5 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 hover:bg-gray-100 text-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Country of Origin */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Country of Origin</label>
                    <CountrySelector
                      value={importInfo.countryOfOrigin}
                      onChange={handleCountryChange}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs bg-white text-gray-900 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {/* Client's suggested country of origin */}
                    {clientSuggestedCountry && (
                      <div className="mt-1.5 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                        <span className="font-medium">Client expects:</span> {clientSuggestedCountry.label}
                      </div>
                    )}
                  </div>

                  {/* Photos */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Photos</label>
                    <label className="w-full text-xs bg-gray-100 text-gray-700 px-2 py-1.5 rounded-md border border-gray-300 hover:bg-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer block text-center">
                      Upload Photos
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => {
                          Array.from(e.target.files).forEach(file => handlePhotoAdd(file));
                        }}
                        className="hidden"
                      />
                    </label>

                    {/* Photo Thumbnails */}
                    {importInfo.photos && importInfo.photos.length > 0 ? (
                      <div className="mt-1.5 grid grid-cols-4 gap-1.5 max-h-24 overflow-y-auto border border-gray-200 p-1.5 rounded-md bg-gray-50">
                        {importInfo.photos.map((photo, index) => (
                          <div key={index} className="relative group aspect-square">
                            <img
                              src={photo.url || photo}
                              alt={`Photo ${index + 1}`}
                              className="w-full h-full object-cover rounded border"
                              onClick={() => handlePhotoClick(photo, index)}
                            />
                            <button
                              onClick={() => handlePhotoRemove(index)}
                              className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity transform translate-x-1/2 -translate-y-1/2"
                            >
                              <svg className="h-2.5 w-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-1.5 text-xs text-gray-500 text-center py-2 bg-gray-50 border border-gray-200 rounded">No photos uploaded</div>
                    )}
                  </div>

                  {/* Modal Actions */}
                  <div className="flex space-x-2 pt-1">
                    <button
                      onClick={() => setShowCustomsModal(false)}
                      className="flex-1 text-xs px-2 py-1.5 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        // Save customs info logic here
                        setShowCustomsModal(false);
                      }}
                      className="flex-1 text-xs px-2 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                    >
                      Save
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Print Buttons */}
          <div className="space-y-1.5 mt-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleQuickPrint(1);
              }}
              className="w-16 text-blue-600 hover:text-blue-700 font-semibold py-1 text-xs rounded-md bg-blue-50 border border-blue-200 hover:bg-blue-100 text-center transition-colors"
              title="Quick Print 1 Label"
            >
              Print 1
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleQuickPrint(totalQuantity);
              }}
              className="w-16 text-green-600 hover:text-green-700 font-semibold py-1 text-xs rounded-md bg-green-50 border border-green-200 hover:bg-green-100 text-center transition-colors"
              title="Quick Print All Labels"
            >
              Print {totalQuantity}
            </button>

            {/* Advanced Print Options Button */}
            <button
              onClick={(event) => handlePrintClick(event)}
              className="w-16 text-gray-600 hover:text-gray-700 font-semibold py-1 text-xs rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-center"
            >
              Print
            </button>
          </div>

          {/* FNSKU Sync */}
          {(!item.item.fnsku || item.item.fnsku === '' || item.item.fnsku === 'UNKNOWN') && (
            <button
              onClick={handleSyncFnsku}
              disabled={isSyncingFnsku}
              className="w-16 text-gray-600 hover:text-gray-800 font-semibold py-1 text-xs rounded-md border border-gray-300 bg-white hover:bg-gray-100 text-center mt-2"
              title="Sync FNSKU"
            >
              {isSyncingFnsku ? (
                <svg className="animate-spin h-3 w-3 mx-auto" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
              ) : (
                'Sync'
              )}
            </button>
          )}
        </div>
      </div>

      {/* Print Labels Dropdown (New) */}
      {showPrintDropdown && selectedPrintItem && (
        <div 
          ref={printDropdownRef}
          className="fixed w-80 z-[9999] rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
          style={{
            top: `${printDropdownPosition.top}px`,
            left: `${printDropdownPosition.left}px`,
            maxHeight: '80vh',
            overflow: 'hidden'
          }}
        >
          <div className="p-3 border-b border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-gray-800">
                {printStep === 'config' ? 'Print Labels' : 'Select Printer'}
              </div>
              <button 
                onClick={resetPrintDropdown}
                className="p-1 hover:bg-gray-200 rounded text-gray-500 hover:text-gray-800"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            {printStep === 'printer' && (
              <button 
                onClick={handlePrintBack}
                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                ← Back to Configuration
              </button>
            )}
          </div>
          
          <div className="py-1 max-h-96 overflow-y-auto">
            {printStep === 'config' ? (
              <>
                {/* Quantity Section */}
                <div className="px-3 py-1.5">
                  <div className="text-xs font-medium text-gray-600 mb-1.5">Quantity to Print</div>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center cursor-pointer text-xs text-gray-700">
                      <input
                        type="radio"
                        name="printQuantity"
                        value="1"
                        checked={printQuantity === 1}
                        onChange={() => setPrintQuantity(1)}
                        className="form-radio h-3.5 w-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="ml-2">Print 1</span>
                    </label>
                    <label className="flex items-center cursor-pointer text-xs text-gray-700">
                      <input
                        type="radio"
                        name="printQuantity"
                        value="custom"
                        checked={printQuantity === 'custom'}
                        onChange={() => setPrintQuantity('custom')}
                        className="form-radio h-3.5 w-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="ml-2">Print:</span>
                    </label>
                    <input
                      type="number"
                      value={customPrintQty}
                      onChange={(e) => setCustomPrintQty(e.target.value)}
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-xs text-center"
                      min="1"
                      placeholder="100"
                      onClick={() => setPrintQuantity('custom')}
                    />
                  </div>
                </div>

                {/* Separator Section */}
                <div className="px-3 py-1.5 border-t border-gray-100">
                  <label className="flex items-center cursor-pointer text-xs text-gray-700">
                    <input
                      type="checkbox"
                      checked={useSeparator}
                      onChange={(e) => setUseSeparator(e.target.checked)}
                      className="form-checkbox h-3.5 w-3.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="ml-2">Add separator every:</span>
                    <input
                      type="number"
                      value={separatorInterval}
                      onChange={(e) => setSeparatorInterval(parseInt(e.target.value) || 10)}
                      className="ml-2 mr-2 w-12 px-2 py-1 border border-gray-300 rounded text-xs text-center"
                      min="1"
                      disabled={!useSeparator}
                    />
                    <span>labels</span>
                  </label>
                </div>

                {/* Extra Labels Section */}
                <div className="px-3 py-1.5 border-t border-gray-100">
                  <div className="text-xs font-medium text-gray-600 mb-1.5">Insert Extra Labels</div>
                  <div className="space-y-1">
                    {['none', 'Ready to Ship', 'Sold as a Set', 'Suffocation Warning'].map((option) => (
                      <label key={option} className="flex items-center cursor-pointer text-xs text-gray-700 w-full">
                        <input
                          type="radio"
                          name="extraLabel"
                          value={option}
                          checked={extraLabelType === option}
                          onChange={() => setExtraLabelType(option)}
                          className="form-radio h-3.5 w-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
                          disabled={sequenceMode}
                        />
                        <span className={`ml-2 ${sequenceMode ? 'text-gray-400' : 'text-gray-700'}`}>
                          {option === 'none' ? 'None' : option}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Sequence Mode Section */}
                <div className="px-3 py-1.5 border-t border-gray-100">
                  <label className="flex items-center cursor-pointer text-xs text-gray-700 mb-1.5">
                    <input
                      type="checkbox"
                      checked={sequenceMode}
                      onChange={(e) => {
                        setSequenceMode(e.target.checked);
                        if (e.target.checked) {
                          setExtraLabelType('none');
                        }
                      }}
                      className="form-checkbox h-3.5 w-3.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="ml-2">Enable sequence mode</span>
                  </label>
                  
                  {sequenceMode && (
                    <div className="ml-5 text-xs text-gray-600 flex items-center">
                      <span>For every 1 item label, print</span>
                      <input
                        type="number"
                        value={sequenceCount}
                        onChange={(e) => setSequenceCount(parseInt(e.target.value) || 3)}
                        className="mx-2 w-12 px-2 py-1 border border-gray-300 rounded text-xs text-center"
                        min="1"
                      />
                      <span>× Ready to Ship</span>
                    </div>
                  )}
                </div>
              </>
            ) : (
              /* Printer Selection Step */
              <div className="px-3 py-1.5">
                {loadingPrinters ? (
                  <div className="flex items-center justify-center py-4 text-xs text-gray-500">
                    <span>Loading printers...</span>
                  </div>
                ) : availablePrinters.length === 0 ? (
                  <div className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded">
                    No printers available. Please check your printer settings.
                  </div>
                ) : (
                  <div className="space-y-1 max-h-80 overflow-y-auto">
                    {availablePrinters.map((printer) => (
                      <button
                        key={printer.id}
                        onClick={() => {
                          setSelectedPrinter(printer.id);
                          // We need to call execute directly after setting state
                          // but state updates are async. A temporary solution is to pass it.
                          handlePrintExecute(printer.id); 
                        }}
                        className="w-full text-left px-3 py-2 text-xs bg-white border border-gray-200 rounded hover:bg-blue-50 hover:border-blue-300 active:bg-blue-100 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-30 group"
                      >
                        <div className="flex items-center">
                          <div className="w-2 h-2 bg-green-400 rounded-full mr-2 group-hover:bg-blue-500 transition-colors duration-150"></div>
                          <div className="font-medium text-gray-800 group-hover:text-blue-700 transition-colors duration-150 truncate">{printer.name}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {printStep === 'config' && (
            <div className="border-t border-gray-100 px-3 py-2">
              <button
                onClick={handlePrintNext}
                className="w-full text-xs bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500"
              >
                {localStorage.getItem('autoPrintEnabled') === 'true' ? 'Next →' : 'Print Labels'}
              </button>
            </div>
          )}
        </div>
      )}


      {/* Delete Confirmation Dialog */}
      <Dialog open={deletePhotoConfirm.show} onOpenChange={(open) => setDeletePhotoConfirm({ show: open, index: open ? deletePhotoConfirm.index : null })}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center text-gray-900">
              <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Delete Photo
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-700">
              Are you sure you want to delete this origin proof photo? This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button 
              variant="outline"
              onClick={() => setDeletePhotoConfirm({ show: false, index: null })}
            >
              Cancel
            </Button>
            <Button 
              onClick={() => handlePhotoDelete(deletePhotoConfirm.index)} 
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Sequence Dialog */}
      <Dialog open={showSequenceDialog} onOpenChange={setShowSequenceDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Print with Sequence</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-gray-700">
              How many labels do you need per product (including the FNSKU):
            </p>
            <input
              type="number"
              value={sequenceCount}
              onChange={(e) => setSequenceCount(Math.max(2, parseInt(e.target.value) || 2))}
              className="w-full px-3 py-2 border border-gray-300 rounded text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-opacity duration-50"
              min="2"
              placeholder="Labels per product"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSequenceDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                const qty = parseInt(document.getElementById(`customQty-${item.item.merchant_sku}`).value, 10);
                handleSequencePrint(qty, sequenceCount);
              }}
            >
              Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={showImagePreview} onOpenChange={setShowImagePreview}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] w-auto h-auto relative p-0 overflow-hidden fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <button
            onClick={() => setShowImagePreview(false)}
            className="absolute top-2 right-2 p-2 rounded-full bg-black bg-opacity-50 text-white hover:bg-opacity-70 transition-opacity duration-50 z-10"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={currentImageUrl}
            alt={item.item.title || 'Product image'}
            className="max-w-full max-h-[90vh] w-auto h-auto object-contain"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://qph.cf2.quoracdn.net/main-qimg-654617264f9192ec976abe6e53356240-lq';
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Add Photo Preview Dialog */}
      <Dialog open={selectedPhotoPreview.show} onOpenChange={(open) => setSelectedPhotoPreview({ show: open, photo: open ? selectedPhotoPreview.photo : null, index: open ? selectedPhotoPreview.index : null })}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] w-auto h-auto relative p-0 overflow-hidden fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <div className="absolute top-2 right-2 flex gap-2 z-10">
            {/* Delete button */}
            <button
              onClick={handlePhotoPreviewDelete}
              className="p-2 rounded-full bg-red-500 text-white hover:bg-red-600 transition-opacity duration-50"
              title="Delete photo"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
            {/* Close button */}
            <button
              onClick={() => setSelectedPhotoPreview({ show: false, photo: null, index: null })}
              className="p-2 rounded-full bg-black bg-opacity-50 text-white hover:bg-opacity-70 transition-opacity duration-50"
              title="Close preview"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <img
            src={selectedPhotoPreview.photo?.url || selectedPhotoPreview.photo}
            alt="Origin proof preview"
            className="max-w-full max-h-[90vh] w-auto h-auto object-contain"
          />
        </DialogContent>
      </Dialog>

      {/* Add Printer Selection Dialog */}
      <Dialog 
        open={showPrinterDialog} 
        onOpenChange={(open) => {
          if (!open) {
            console.log('🔄 Dialog closing');
            setShowPrinterDialog(false);
            setPendingPrintJob(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Select Printer</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-gray-700">
              Choose which printer to use for this print job:
            </p>
            
            {/* Debug logs removed */}
            
            {availablePrinters.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No printers available. Make sure the print server is running.
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {availablePrinters.map((printer, index) => {
                  return (
                    <button
                      key={printer.id || index}
                      onClick={() => handlePrinterSelect(printer)}
                      className="w-full text-left p-3 border border-gray-300 rounded-lg hover:bg-blue-50 hover:border-blue-500 transition-opacity duration-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                    >
                      <div className="font-semibold text-gray-900">{printer.name}</div>
                      <div className="text-sm text-gray-600">
                        ID: {printer.id}
                        {printer.description && printer.description !== printer.name && ` • ${printer.description}`}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline"
              onClick={() => {
                setShowPrinterDialog(false);
                setPendingPrintJob(null);
              }}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Previous Configurations</span>
              <button 
                onClick={() => setShowHistoryDialog(false)}
                className="p-1 hover:bg-gray-200 rounded text-gray-500 hover:text-gray-800"
              >
                <X className="w-4 h-4" />
              </button>
            </DialogTitle>
            <div className="text-sm text-gray-700 break-words" title={historyData.title}>
              {historyData.title}
            </div>
            <div className="text-xs text-gray-500">ASIN: {historyData.asin}</div>
          </DialogHeader>

          {/* History notification */}
          {historyNotification.show && (
            <div className="mx-6 p-2 bg-green-50 text-green-700 rounded-md border border-green-200 text-xs">
              {historyNotification.message}
            </div>
          )}

          <div className="px-6 pb-6 overflow-y-auto">
            {historyData.loading ? (
              <div className="flex justify-center items-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
              </div>
            ) : (
              <div className="space-y-2">
                {historyData.history.map((record, index) => {
                  const historicalItemsMap = new Map(
                    (record.ASIN_LIST || '').split(',')
                      .filter(item => item && item.includes(':'))
                      .map(item => {
                        const [asin, qty] = item.split(':');
                        return [asin.trim(), parseInt(qty.trim(), 10)];
                      })
                  );
                  const thisItemQty = historicalItemsMap.get(historyData.asin);
                  const otherSkusCount = historicalItemsMap.size - 1;
                  
                  const firstInstance = (record.BOX_INSTANCES || '').split(',')[0];
                  const [outboundId, boxNum] = firstInstance ? firstInstance.split(':') : [];

                  const otherItems = Array.from(historicalItemsMap.entries())
                    .filter(([asin]) => asin !== historyData.asin);

                  return (
                    <div 
                      key={`${record.BOX_INSTANCES}-${index}`}
                      className="p-3 border border-gray-200 rounded-lg bg-white shadow-sm hover:border-blue-400 transition-all"
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-800 text-sm">
                            {record.LENGTH}×{record.WIDTH}×{record.HEIGHT} cm • {record.WEIGHT} kg
                          </div>
                          <div className="text-xs text-gray-600 truncate">
                            This item: <span className="font-medium">{thisItemQty !== undefined ? `${thisItemQty} units` : 'N/A'}</span>
                            {otherSkusCount > 0 && ` • ${otherSkusCount} other SKU(s)`}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {outboundId ? (
                              <>
                                Used in <a href={`https://yyzprep.ca/outbounds/${outboundId}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Outbound {outboundId}</a> (Box #{boxNum})
                              </>
                            ) : `Used ${record.FREQUENCY} time(s)`} • Last: {new Date(record.LAST_USED).toLocaleDateString()}
                          </div>
                        </div>
                        <Button
                          onClick={() => handleCreateBoxFromConfig(record)}
                          size="sm"
                          className="ml-3 h-8 px-3 text-xs shrink-0"
                        >
                          <Copy className="w-3.5 h-3.5 mr-1.5" />
                          Create Box
                        </Button>
                      </div>
                      {otherItems.length > 0 && (
                        <button
                          onClick={() => setExpandedHistoryIndex(expandedHistoryIndex === index ? null : index)}
                          className="text-xs text-blue-600 hover:underline mt-2 flex items-center gap-1"
                        >
                          {expandedHistoryIndex === index ? 'Hide details' : `View ${otherItems.length} other item(s)`}
                          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expandedHistoryIndex === index ? 'rotate-180' : ''}`} />
                        </button>
                      )}
                      {expandedHistoryIndex === index && (
                        <div className="mt-2 p-2 bg-gray-50 border rounded-md animate-in fade-in-50">
                          <div className="text-xs font-medium text-gray-700 mb-2">Other items in this configuration:</div>
                          <ul className="space-y-1.5">
                            {otherItems.map(([asin, qty]) => {
                                const isAvailable = shipmentItems.some(si => si.item?.asin === asin);
                                const productInfo = getProductInfo({ ASIN: asin }, getAsinData(asin));
                                return (
                                    <li key={asin} className="flex items-center justify-between text-xs">
                                        <div className="flex items-center min-w-0">
                                            <span 
                                              className={`w-2 h-2 rounded-full mr-2 flex-shrink-0 ${isAvailable ? 'bg-green-500' : 'bg-gray-400'}`} 
                                              title={isAvailable ? 'Available in current shipment' : 'Not in current shipment'}
                                            ></span>
                                            <span className="text-gray-800 truncate" title={productInfo.title || asin}>
                                              {productInfo.title && productInfo.title !== 'Product title not available' ? productInfo.title : asin}
                                            </span>
                                        </div>
                                        <span className="text-gray-600 font-medium ml-2 flex-shrink-0">x{qty}</span>
                                    </li>
                                )
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {historyData.history.length === 0 && !historyData.loading && (
                  <div className="text-center text-gray-600 py-6 text-xs">
                    No previous configurations found
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Snackbar removed - using toast system now */}
    </div>
  );
}

export default OutboundItem;
